import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { 
  FiArrowLeft, 
  FiDownload, 
  FiEye, 
  FiCalendar,
  FiUsers,
  FiBarChart2,
  FiRefreshCw,
  FiChevronDown,
  FiFileText,
  FiFile,
  FiExternalLink,
  FiTable,
  FiImage,
  FiFilter,
  FiArrowUp,
  FiArrowDown,
  FiX,
  FiCheckCircle,
  FiUser,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiSettings,
  FiAward
} from 'react-icons/fi';
import { formsAPI, responsesAPI, certificatesAPI } from '../services/api';
import { isQuestionnaire, getRemark } from '../utils/questionnaireScoring';
import JSZip from 'jszip';

const ResponsesContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 60px 20px;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 48px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  padding: 32px;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  color: #374151;
  text-decoration: none;
  font-weight: 600;
  font-size: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  }
`;

const HeaderInfo = styled.div`
  flex: 1;
  margin-left: 24px;
`;

const FormTitle = styled.h1`
  font-size: 10px;
  font-weight: 800;
  background: black;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
  margin-bottom: 12px;
`;

const FormDescription = styled.p`
  color: #6b7280;
  font-size: 9px;
  font-weight: 400;
  line-height: 1.6;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #374151;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  font-size: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #f8fafc;
    border-color: #3b82f6;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const ExportDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const ExportButton = styled(ActionButton)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-color: #10b981;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);

  &:hover {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    border-color: #059669;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 200px;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: #374151;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f3f4f6;
  }

  &:first-child {
    border-radius: 6px 6px 0 0;
  }

  &:last-child {
    border-radius: 0 0 6px 6px;
  }
`;

const DropdownIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${props => props.color || '#6b7280'};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(135deg, ${props => props.color || 'black'} 0%, ${props => props.color || 'black'} 100%);
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const StatValue = styled.div`
  font-size: 14px;
  font-weight: 800;
  background: black;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
`;

const StatLabel = styled.div`
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ResponsesTable = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const TableHeader = styled.div`
  padding: 40px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%);
`;

const TableTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  background: black;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
`;

const Table = styled.div`
  overflow-x: auto;
`;

const TableContent = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: #f9fafb;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 10px;
`;

const TableCell = styled.td`
  padding: 16px;
  color: #374151;
  font-size: 10px;
`;

const ResponseId = styled.span`
  font-family: monospace;
  font-size: 10px;
  color: #6b7280;
`;

const DateCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 10px;
`;

const ViewButton = styled.button`
  padding: 6px 12px;
  background: #eff6ff;
  color: #374151;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #dbeafe;
  }
`;

const Pagination = styled.div`
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
`;

const PageInfo = styled.div`
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  min-width: 40px;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #9ca3af;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${props => props.active && `
    background: black;
    color: white;
    border-color: black;
    font-weight: 600;
    
    &:hover {
      background: #374151;
      border-color: #374151;
    }
  `}
`;

const PageSizeSelector = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #9ca3af;
    background: #f9fafb;
  }

  &:focus {
    outline: none;
    border-color: black;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const PaginationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 400px;
  gap: 20px;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid black;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

const AnalyticsSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid ${props => props.color || '#8b5cf6'};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  display: inline-block;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyStateTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
`;

const EmptyStateDescription = styled.p`
  margin-bottom: 24px;
`;

const ResponseModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 0;
  max-width: 700px;
  max-height: 85vh;
  overflow: hidden;
  margin: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  background: black;
  color: white;
  border-radius: 16px 16px 0 0;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  font-size: 24px;
  color: white;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const ModalBody = styled.div`
  padding: 32px;
  max-height: calc(85vh - 120px);
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const ResponseField = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`;

const ResponseFieldLabel = styled.div`
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResponseFieldValue = styled.div`
  color: #475569;
  padding: 12px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-size: 16px;
  line-height: 1.5;
  word-break: break-word;
  min-height: 20px;
`;

const SubmissionInfo = styled.div`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SubmissionIcon = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const SubmissionDetails = styled.div`
  flex: 1;
`;

const SubmissionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 4px 0;
`;

const SubmissionDate = styled.p`
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
`;

// Additional modal components needed for the ResponseModal
const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 4px solid black;
`;

const FieldLabel = styled.div`
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  font-size: 14px;
`;

const FieldValue = styled.div`
  color: #1f2937;
  font-size: 15px;
  line-height: 1.5;
  word-break: break-word;
`;

const FileViewer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 4px;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #374151;
`;

const FileName = styled.span`
  font-weight: 500;
`;

const FileSize = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const ViewFileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: black;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #374151;
  }
`;

const FileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const OpenViewFileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;


const ModalFooter = styled.div`
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
`;

// Professional Filter Controls
const FilterControls = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: black;
  }
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
`;

const FilterLabel = styled.label`
  font-size: 10px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SortDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.1), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    border-color: black;
    background: rgba(255, 255, 255, 1);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  }
`;

const SortMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 50;
  min-width: 200px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  animation: ${props => props.isOpen ? 'slideDown' : 'slideUp'} 0.3s ease;
  overflow: hidden;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideUp {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-10px);
    }
  }
`;

const SortOption = styled.button`
  width: 100%;
  text-align: left;
  padding: 14px 18px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: black;
  }

  &:first-child {
    border-radius: 16px 16px 0 0;
  }

  &:last-child {
    border-radius: 0 0 16px 16px;
  }
`;

const DateRangeContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  background: rgba(255, 255, 255, 0.7);
  padding: 8px 16px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const DateInput = styled.input`
  padding: 10px 14px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
  color: #374151;
  background: rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
  min-width: 140px;

  &:focus {
    outline: none;
    border-color: black;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
    background: white;
  }

  &:hover {
    border-color: black;
    background: white;
  }
`;

const DateSeparator = styled.span`
  color: #6b7280;
  font-weight: 600;
  font-size: 14px;
  padding: 0 4px;
`;

const ClearFiltersButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
  }
`;

const ActiveFilters = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  background: rgba(0, 0, 0, 0.05);
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  margin-top: 16px;
`;

const ActiveFiltersLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-right: 8px;
`;

const FilterTag = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: black;
  color: white;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }
`;

const RemoveFilterButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

// Professional Tab Components
const TabContainer = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  overflow: hidden;
  margin-bottom: 32px;
`;

const TabHeader = styled.div`
  display: flex;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
  padding: 0 8px;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }
`;

const TabButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  border: none;
  background: transparent;
  color: ${props => props.active ? '#1e40af' : '#64748b'};
  font-size: 10px;
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  white-space: nowrap;
  border-radius: 8px;
  margin: 8px 4px;
  min-width: 120px;
  justify-content: center;

  &:hover {
    background: ${props => props.active ? '#dbeafe' : '#f1f5f9'};
    color: ${props => props.active ? '#1e40af' : '#475569'};
  }

  ${props => props.active && `
    background: #dbeafe;
    color: #1e40af;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -9px;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 3px;
      background: linear-gradient(90deg, black, #1d4ed8);
      border-radius: 2px;
    }
  `}
`;

const TabContent = styled.div`
  padding: 32px;
  min-height: 400px;
  background: white;
`;

const SettingsForm = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const SettingsSection = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
`;

const SettingsTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 12px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: black;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 12px;
  background: white;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: black;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const SaveButton = styled.button`
  padding: 12px 24px;
  background: black;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #374151;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ExpiryStatus = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 12px;
  font-size: 14px;
  font-weight: 500;
  background: ${props => {
    switch(props.status) {
      case 'active': return '#f0fdf4';
      case 'expired': return '#fef2f2';
      case 'inactive': return '#f3f4f6';
      default: return '#f0fdf4';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'active': return '#166534';
      case 'expired': return '#dc2626';
      case 'inactive': return '#6b7280';
      default: return '#166534';
    }
  }};
  border: 1px solid ${props => {
    switch(props.status) {
      case 'active': return '#bbf7d0';
      case 'expired': return '#fecaca';
      case 'inactive': return '#d1d5db';
      default: return '#bbf7d0';
    }
  }};
`;

const TabIcon = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  border-radius: 4px;
  background: ${props => props.active ? 'rgba(59, 130, 246, 0.1)' : 'transparent'};
  color: ${props => props.active ? 'black' : '#64748b'};
  transition: all 0.2s ease;
`;

const TabBadge = styled.span`
  background: ${props => props.active ? 'black' : '#e2e8f0'};
  color: ${props => props.active ? 'white' : '#64748b'};
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
  margin-left: 6px;
  transition: all 0.2s ease;
  min-width: 20px;
  text-align: center;
  line-height: 1;
`;

// Missing styled components
const TimeCell = styled.div`
  font-size: 10px;
  color: #9ca3af;
  margin-top: 2px;
`;

const PreviewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  color: black;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #dbeafe;
    border-color: #bfdbfe;
    transform: translateY(-1px);
  }
`;

const ActionButtonStyled = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  min-width: 80px;
  height: 32px;
  flex-shrink: 0;
  cursor: pointer;
  background: ${props => {
    switch(props.variant) {
      case 'primary': return '#eff6ff';
      case 'secondary': return '#f3f4f6';
      case 'danger': return '#fef2f2';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.variant) {
      case 'primary': return 'black';
      case 'secondary': return '#374151';
      case 'danger': return '#dc2626';
      default: return '#374151';
    }
  }};
  border: 1px solid ${props => {
    switch(props.variant) {
      case 'primary': return '#dbeafe';
      case 'secondary': return '#e5e7eb';
      case 'danger': return '#fecaca';
      default: return '#e5e7eb';
    }
  }};

  &:hover {
    background: ${props => {
      switch(props.variant) {
        case 'primary': return '#dbeafe';
        case 'secondary': return '#e5e7eb';
        case 'danger': return '#fee2e2';
        default: return '#e5e7eb';
      }
    }};
    border-color: ${props => {
      switch(props.variant) {
        case 'primary': return '#bfdbfe';
        case 'secondary': return '#d1d5db';
        case 'danger': return '#fca5a5';
        default: return '#d1d5db';
      }
    }};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #d1d5db;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ScoreBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${props => {
    if (props.percentage >= 90) return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
    if (props.percentage >= 80) return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
    if (props.percentage >= 70) return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
    if (props.percentage >= 60) return 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)';
    return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
  }};
  color: white;
  border-radius: 20px;
  font-weight: 600;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

function Responses() {
  const { formId } = useParams();
  
  // Debug route parameters
  useEffect(() => {
    console.log('🔍 Responses component - Route params:', { formId });
    console.log('🔍 Current URL:', window.location.href);
    console.log('🔍 Current pathname:', window.location.pathname);
  }, [formId]);
  
  // Helper function to format date in Kuwait timezone
  const formatKuwaitDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'Asia/Kuwait',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to format time in Kuwait timezone
  const formatKuwaitTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      timeZone: 'Asia/Kuwait',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Helper function to format datetime in Kuwait timezone
  const formatKuwaitDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: formatKuwaitDate(dateString),
      time: formatKuwaitTime(dateString),
      full: date.toLocaleString('en-US', {
        timeZone: 'Asia/Kuwait',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    };
  };

  // Helper function to check if a field is a file upload field
  const isFileField = (fieldId, formStructure) => {
    if (!formStructure || !Array.isArray(formStructure)) return false;
    
    // Check top-level fields first
    let field = formStructure.find(f => f.id === fieldId || f.name === fieldId);
    if (field) {
      return field.type === 'file';
    }
    
    // Check contact fields
    for (const topLevelField of formStructure) {
      if (topLevelField.type === 'contact' && topLevelField.contactFields) {
        const contactField = topLevelField.contactFields.find(cf => cf.name === fieldId || cf.id === fieldId);
        if (contactField) {
          return contactField.type === 'file';
        }
      }
    }
    
    return false;
  };

  // Helper function to parse file names from response data
  const parseFileNames = (fileData) => {
    if (!fileData) return [];
    if (Array.isArray(fileData)) return fileData;
    if (typeof fileData === 'string') {
      return fileData.split(',').map(name => name.trim()).filter(name => name);
    }
    return [];
  };

  // Helper function to get file extension
  const getFileExtension = (filename) => {
    return filename.split('.').pop().toLowerCase();
  };

  const normalizeKey = (key) => {
    if (key === null || key === undefined) return '';
    return String(key).trim().toLowerCase();
  };

const formatKeyLabel = (key) => {
  if (key === null || key === undefined) return 'Field';
  const original = String(key);
  const withSpaces = original
    .replace(/[_\-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  if (!withSpaces) return 'Field';
  return withSpaces
    .split(' ')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const isGeneratedFieldId = (value) => {
  if (!value) return false;
  const normalized = normalizeKey(value);
  return /^field[_-]/i.test(value) || normalized.startsWith('field ') || normalized.startsWith('field-');
};

const getRepeatableGroupPrefix = (field) => {
  const raw =
    field?.label ||
    field?.question ||
    field?.title ||
    field?.name ||
    field?.id ||
    '';
  if (isGeneratedFieldId(raw)) return '';
  return formatKeyLabel(raw);
};

const buildRepeatableLabel = (field, displayNumber, childLabel) => {
  const prefix = getRepeatableGroupPrefix(field) || 'Entry';
  return `${prefix} #${displayNumber} - ${childLabel}`;
};

  const buildFieldIndex = (formStructure = []) => {
    const index = new Map();

    const addMeta = (key, meta) => {
      if (!key) return;
      const normalized = normalizeKey(key);
      if (!normalized) return;
      if (!index.has(normalized)) {
        index.set(normalized, meta);
      }
    };

    const processField = (field, context = {}) => {
      if (!field || typeof field !== 'object') return;

      const meta = {
        field,
        parentRepeatable: context.parentRepeatable || null,
        parentContact: context.parentContact || null,
        parentSection: context.parentSection || null
      };

      addMeta(field.id, meta);
      addMeta(field.name, meta);
      addMeta(field.label, meta);
      addMeta(field.question, meta);
      addMeta(field.title, meta);
      
      // Also index with prefixes that might be used in response data
      if (field.id) {
        addMeta(`field_${field.id}`, meta);
        addMeta(`q_${field.id}`, meta);
      }
      if (field.name) {
        addMeta(`field_${field.name}`, meta);
        addMeta(`q_${field.name}`, meta);
      }

      if (field.type === 'repeatable' && Array.isArray(field.repeatableFields)) {
        field.repeatableFields.forEach((child) => {
          if (!child) return;
          const childMeta = {
            field: child,
            parentRepeatable: field,
            parentContact: null,
            parentSection: context.parentSection || null
          };
          addMeta(child.id, childMeta);
          addMeta(child.name, childMeta);
          addMeta(child.label, childMeta);
          addMeta(child.question, childMeta);
          addMeta(child.title, childMeta);
          processField(child, { parentRepeatable: field, parentSection: context.parentSection || null });
        });
      }

      if (field.type === 'contact' && Array.isArray(field.contactFields)) {
        field.contactFields.forEach((child) => {
          if (!child) return;
          const childMeta = {
            field: child,
            parentRepeatable: null,
            parentContact: field,
            parentSection: context.parentSection || null
          };
          addMeta(child.id, childMeta);
          addMeta(child.name, childMeta);
          addMeta(child.label, childMeta);
          addMeta(child.question, childMeta);
          addMeta(child.title, childMeta);
        });
      }

      if (Array.isArray(field.fields)) {
        field.fields.forEach((child) => {
          processField(child, {
            parentRepeatable: context.parentRepeatable || null,
            parentContact: context.parentContact || null,
            parentSection: context.parentSection || (field.type === 'section' ? field : null)
          });
        });
      }
    };

    if (Array.isArray(formStructure)) {
      formStructure.forEach((field) => processField(field, { parentSection: field?.type === 'section' ? field : null }));
    }

    return index;
  };

  const resolveFieldMeta = (fieldIndex, key) => {
    if (!key) return null;
    const normalized = normalizeKey(key);
    if (!normalized) return null;

    if (fieldIndex.has(normalized)) {
      return fieldIndex.get(normalized);
    }

    if (normalized.endsWith('_comment')) {
      const baseKey = key.slice(0, -8);
      const baseMeta = resolveFieldMeta(fieldIndex, baseKey);
      if (baseMeta) {
        return { ...baseMeta, isComment: true };
      }
    }

    const repeatableMatch = key.match(/^(.+?)_(\d+)_(.+)$/);
    if (repeatableMatch) {
      const [, parentKey, instanceIndexStr, childKey] = repeatableMatch;
      const instanceIndex = parseInt(instanceIndexStr, 10);

      const childMeta = resolveFieldMeta(fieldIndex, childKey);
      if (childMeta && childMeta.parentRepeatable) {
        return { ...childMeta, instanceIndex };
      }

      const parentMeta = resolveFieldMeta(fieldIndex, parentKey);
      if (parentMeta && parentMeta.field?.type === 'repeatable' && Array.isArray(parentMeta.field.repeatableFields)) {
        const nestedField = parentMeta.field.repeatableFields.find((rf) => {
          const rfId = normalizeKey(rf.id);
          const rfName = normalizeKey(rf.name);
          const childNormalized = normalizeKey(childKey);
          return childNormalized === rfId || childNormalized === rfName;
        });

        if (nestedField) {
          return {
            field: nestedField,
            parentRepeatable: parentMeta.field,
            parentContact: null,
            parentSection: parentMeta.parentSection || null,
            instanceIndex
          };
        }
      }
    }

    const partialEntry = Array.from(fieldIndex.entries()).find(([storedKey]) => {
      if (!storedKey) return false;
      return storedKey.includes(normalized) || normalized.includes(storedKey);
    });

    return partialEntry ? partialEntry[1] : null;
  };

  const getFieldLabelFromMeta = (meta, fallbackLabel) => {
    // If fallbackLabel is provided and is not empty, use it (it should be the field's label from form structure)
    if (fallbackLabel && fallbackLabel !== 'Unnamed Field' && typeof fallbackLabel === 'string' && fallbackLabel.trim()) {
      // The fallbackLabel is already the field's label from the form structure
      // Just handle special cases (comments, repeatables, etc.) and return it
      let label = fallbackLabel.trim();
      
      if (meta && meta.isComment) {
        const commentLabel = formatKeyLabel(meta.field?.commentLabel || 'Comments');
        return `${label} - ${commentLabel}`;
      }
      
      if (meta && meta.parentContact?.type === 'contact') {
        let rawParent =
          meta.parentContact.label ||
          meta.parentContact.question ||
          meta.parentContact.title ||
          meta.parentContact.name ||
          'Contact';
        if (isGeneratedFieldId(rawParent)) {
          rawParent = 'Contact';
        }
        const parentLabel = formatKeyLabel(rawParent);
        return `${parentLabel} - ${label}`;
      }
      
      if (meta && meta.parentRepeatable) {
        const displayNumber =
          Number.isFinite(meta.instanceDisplayNumber) ? meta.instanceDisplayNumber :
          Number.isFinite(meta.instanceIndex) ? (meta.instanceIndex + 1) :
          1;
        return buildRepeatableLabel(meta.parentRepeatable, displayNumber, label);
      }
      
      return label;
    }
    
    // If no fallbackLabel provided, try to get from meta.field
    if (!meta || !meta.field) {
      return fallbackLabel || 'Unnamed Field';
    }

    // Try to get label from field
    let baseLabel = meta.field.label ||
      meta.field.question ||
      meta.field.title ||
      meta.field.placeholder ||
      meta.field.name ||
      null;

    // NEVER use field ID as label
    if (baseLabel) {
      const baseNormalized = normalizeKey(baseLabel);
      const idNormalized = normalizeKey(meta.field.id || '');
      const nameNormalized = normalizeKey(meta.field.name || '');
      
      // If the label matches the ID or name, it's not a real label
      if (baseNormalized === idNormalized || baseNormalized === nameNormalized) {
        baseLabel = fallbackLabel || 'Unnamed Field';
      }
    } else {
      baseLabel = fallbackLabel || 'Unnamed Field';
    }

    // Final check: if baseLabel looks like a generated ID, use fallback
    if (isGeneratedFieldId(baseLabel)) {
      baseLabel = fallbackLabel || 'Unnamed Field';
    }

    if (meta.isComment) {
      const commentLabel = formatKeyLabel(meta.field?.commentLabel || 'Comments');
      return `${baseLabel} - ${commentLabel}`;
    }

    if (meta.parentContact?.type === 'contact') {
      let rawParent =
        meta.parentContact.label ||
        meta.parentContact.question ||
        meta.parentContact.title ||
        meta.parentContact.name ||
        meta.parentContact.id ||
        'Contact';
      if (isGeneratedFieldId(rawParent)) {
        rawParent = 'Contact';
      }
      const parentLabel = formatKeyLabel(rawParent);
      return `${parentLabel} - ${baseLabel}`;
    }

    if (meta.parentRepeatable) {
      const displayNumber =
        Number.isFinite(meta.instanceDisplayNumber) ? meta.instanceDisplayNumber :
        Number.isFinite(meta.instanceIndex) ? (meta.instanceIndex + 1) :
        1;
      return buildRepeatableLabel(meta.parentRepeatable, displayNumber, baseLabel);
    }

    return baseLabel;
  };

  const getRepeatableInstanceIndices = (responseData, field) => {
    const indices = new Set();
    if (!responseData || typeof responseData !== 'object' || !field) {
      return [];
    }

    const prefixes = [];
    if (field.id) prefixes.push(`${field.id}_`);
    if (field.name) prefixes.push(`${field.name}_`);

    Object.keys(responseData).forEach((key) => {
      if (!key) return;
      for (const prefix of prefixes) {
        if (key.startsWith(prefix)) {
          const remainder = key.slice(prefix.length);
          const instancePart = remainder.split('_')[0];
          const index = parseInt(instancePart, 10);
          if (!isNaN(index)) {
            indices.add(index);
          }
          break;
        }
      }
    });

    return Array.from(indices).sort((a, b) => a - b);
  };

  const extractValueWithKey = (responseData, keys, seenKeys) => {
    if (!responseData || typeof responseData !== 'object') {
      return { value: '', matchedKey: null };
    }

    const dataKeys = Object.keys(responseData);

    for (const key of keys) {
      if (!key) continue;
      
      // Direct match
      if (Object.prototype.hasOwnProperty.call(responseData, key)) {
        if (seenKeys) seenKeys.add(key);
        return { value: responseData[key], matchedKey: key };
      }
      
      // Try normalized match
      const normalized = normalizeKey(key);
      const directMatch = dataKeys.find((existingKey) => normalizeKey(existingKey) === normalized);
      if (directMatch) {
        if (seenKeys) seenKeys.add(directMatch);
        return { value: responseData[directMatch], matchedKey: directMatch };
      }
      
      // Try matching with prefixes (e.g., "field_1763966410378" matches "1763966410378")
      const keyWithoutPrefix = String(key).replace(/^(field_|q_)/i, '');
      if (keyWithoutPrefix !== String(key)) {
        // Try exact match with the ID without prefix
        if (Object.prototype.hasOwnProperty.call(responseData, keyWithoutPrefix)) {
          if (seenKeys) seenKeys.add(keyWithoutPrefix);
          return { value: responseData[keyWithoutPrefix], matchedKey: keyWithoutPrefix };
        }
        // Try with prefixes
        for (const prefix of ['field_', 'q_']) {
          const prefixedKey = prefix + keyWithoutPrefix;
          if (Object.prototype.hasOwnProperty.call(responseData, prefixedKey)) {
            if (seenKeys) seenKeys.add(prefixedKey);
            return { value: responseData[prefixedKey], matchedKey: prefixedKey };
          }
        }
      } else {
        // Try adding prefixes to the key
        for (const prefix of ['field_', 'q_']) {
          const prefixedKey = prefix + key;
          if (Object.prototype.hasOwnProperty.call(responseData, prefixedKey)) {
            if (seenKeys) seenKeys.add(prefixedKey);
            return { value: responseData[prefixedKey], matchedKey: prefixedKey };
          }
        }
      }
    }

    // If no match found, try partial matching
    for (const key of keys) {
      if (!key) continue;
      const normalized = normalizeKey(key);
      const partialMatch = dataKeys.find((existingKey) => {
        const existingNormalized = normalizeKey(existingKey);
        // Remove prefixes for comparison
        const keyWithoutPrefix = String(key).replace(/^(field_|q_)/i, '');
        const existingWithoutPrefix = existingKey.replace(/^(field_|q_)/i, '');
        return existingNormalized.includes(normalized) || 
               normalizeKey(keyWithoutPrefix) === normalizeKey(existingWithoutPrefix);
      });
      if (partialMatch) {
        if (seenKeys) seenKeys.add(partialMatch);
        return { value: responseData[partialMatch], matchedKey: partialMatch };
      }
    }

    return { value: '', matchedKey: null };
  };

  const extractRepeatableValue = (responseData, parentField, instanceValue, repeatableField, fallbackKeys, seenKeys) => {
    if (!responseData || typeof responseData !== 'object') {
      return { value: '', matchedKey: null };
    }

    const prefixes = [];
    if (parentField?.id) prefixes.push(`${parentField.id}_${instanceValue}_`);
    if (parentField?.name) prefixes.push(`${parentField.name}_${instanceValue}_`);

    const childTokens = [
      repeatableField?.id,
      repeatableField?.name,
      repeatableField?.label,
      repeatableField?.question,
      repeatableField?.title
    ].filter(Boolean).map(normalizeKey);

    if (prefixes.length > 0 && childTokens.length > 0) {
      for (const key of Object.keys(responseData)) {
        if (!key) continue;
        if (!prefixes.some(prefix => key.startsWith(prefix))) continue;
        const normalizedKey = normalizeKey(key);
        if (childTokens.some(token => token && normalizedKey.includes(token))) {
          if (seenKeys) seenKeys.add(key);
          return { value: responseData[key], matchedKey: key };
        }
      }
    }

    return extractValueWithKey(responseData, fallbackKeys, seenKeys);
  };

  const formatValueForDisplay = (value) => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) {
      return value.length ? value : '';
    }
    if (typeof value === 'object') {
      return value;
    }
    return String(value);
  };

  const getMatrixRows = (field) => {
    if (Array.isArray(field.matrixRows) && field.matrixRows.length > 0) return field.matrixRows;
    const count = Number.isFinite(field.matrixRowsCount) ? Math.max(1, field.matrixRowsCount) : 10;
    return Array.from({ length: count }, (_, i) => `row_${i}`);
  };

  const getMatrixCols = (field) => {
    if (Array.isArray(field.matrixColumns) && field.matrixColumns.length > 0) return field.matrixColumns;
    const count = Number.isFinite(field.matrixColumnsCount) ? Math.max(1, field.matrixColumnsCount) : 5;
    return Array.from({ length: count }, (_, i) => ({ key: `col_${i + 1}`, label: `Column ${i + 1}` }));
  };

  // If older responses stored default placeholder values like "Option 1", map them to the current option text by index.
  const mapPlaceholderOptionValue = (value, field) => {
    if (!field || !Array.isArray(field.options) || field.options.length === 0) return value;

    const mapOne = (v) => {
      if (typeof v !== 'string') return v;
      const trimmed = v.trim();

      // Handle multiple values stored in one string (common for checkbox): "Option 1, Option 3" or newline-separated.
      if (trimmed.includes(',') || trimmed.includes('\n')) {
        const parts = trimmed
          .split(/[\n,]+/)
          .map((p) => p.trim())
          .filter(Boolean);
        return parts.map(mapOne).join(', ');
      }

      const m = trimmed.match(/^option\s*(\d+)$/i);
      if (!m) return v;
      const idx = Number(m[1]) - 1;
      if (!Number.isFinite(idx) || idx < 0 || idx >= field.options.length) return v;
      return field.options[idx];
    };

    if (Array.isArray(value)) return value.map(mapOne);
    return mapOne(value);
  };

  const buildDisplayFields = (formStructure, responseData, fieldIndex) => {
    const items = [];
    const seenKeys = new Set();

    const iterateFields = (fields) => {
      if (!Array.isArray(fields)) return;

      fields.forEach((field) => {
        if (!field || typeof field !== 'object') return;

        if (field.type === 'section') {
          const rawSectionLabel = field.label || field.name || 'Section';
          const sectionLabel = formatKeyLabel(/^field_/i.test(rawSectionLabel) ? 'Section' : rawSectionLabel);
          const sectionSubtitle = field.sectionSubtitle || '';
          if (Array.isArray(field.fields) && field.fields.length > 0) {
            iterateFields(field.fields);
          }
          return;
        }

        if (field.type === 'repeatable' && Array.isArray(field.repeatableFields)) {
          const instances = getRepeatableInstanceIndices(responseData, field);
          const effectiveInstances = instances.length > 0 ? instances : [0];

          const labelCounters = new Map();

          effectiveInstances.forEach((instanceValue, ordinalIndex) => {
            const displayNumber = ordinalIndex + 1;
            field.repeatableFields.forEach((repeatableField) => {
              if (!repeatableField || repeatableField.type === 'heading' || repeatableField.type === 'subheading') return;
              const repeatableMeta = resolveFieldMeta(fieldIndex, repeatableField.id || repeatableField.name) || {
                field: repeatableField,
                parentRepeatable: field
              };
              const childFallback = formatKeyLabel(repeatableField.label || repeatableField.name || 'Field');
              const currentCount = labelCounters.get(childFallback) || 0;
              labelCounters.set(childFallback, currentCount + 1);
              const effectiveDisplayNumber = currentCount === 0 ? displayNumber : `${displayNumber}.${currentCount + 1}`;
              const label = getFieldLabelFromMeta(
                { ...repeatableMeta, parentRepeatable: field, instanceIndex: ordinalIndex, instanceDisplayNumber: effectiveDisplayNumber },
                buildRepeatableLabel(field, effectiveDisplayNumber, childFallback)
              );
                const fallbackKeys = [
                  `${field.id}_${instanceValue}_${repeatableField.name}`,
                  `${field.name}_${instanceValue}_${repeatableField.name}`,
                  `${field.id}_${instanceValue}_${repeatableField.id}`,
                  `${field.name}_${instanceValue}_${repeatableField.id}`,
                  `${field.id}_${instanceValue}_repeatable_${repeatableField.id}`,
                  `${field.id}_${instanceValue}_repeatable_${repeatableField.id}_${repeatableField.name}`,
                  `${field.name}_${instanceValue}_repeatable_${repeatableField.id}`,
                  `${field.name}_${instanceValue}_repeatable_${repeatableField.id}_${repeatableField.name}`,
                  `${field.id}_${instanceValue}_repeatable_${repeatableField.name}`,
                  `${field.name}_${instanceValue}_repeatable_${repeatableField.name}`
                ];
                const { value: rawValue, matchedKey } = extractRepeatableValue(
                  responseData,
                  field,
                  instanceValue,
                  repeatableField,
                  fallbackKeys,
                  seenKeys
                );
              items.push({
                key: `${field.id || field.name}_${displayNumber}_${repeatableField.id || repeatableField.name}`,
                type: 'repeatable-field',
                label,
                rawValue,
                displayValue: formatValueForDisplay(rawValue),
                field: repeatableField,
                parentRepeatable: field,
                instanceIndex: ordinalIndex,
                instanceDisplayNumber: effectiveDisplayNumber,
                matchedKey
              });
            });
          });
          return;
        }

        if (field.type === 'contact' && Array.isArray(field.contactFields)) {
          field.contactFields.forEach((contactField) => {
            if (!contactField) return;
            const contactMeta = resolveFieldMeta(fieldIndex, contactField.id || contactField.name) || {
              field: contactField,
              parentContact: field
            };
            const parentFallback = formatKeyLabel(field.label || field.name || 'Contact');
            const childFallback = formatKeyLabel(contactField.label || contactField.name || 'Field');
            const label = getFieldLabelFromMeta(
              contactMeta,
              `${parentFallback} - ${childFallback}`
            );
            const { value: rawValue, matchedKey } = extractValueWithKey(responseData, [
              contactField.name,
              contactField.id,
              `${field.id}_${contactField.name}`,
              `${field.name}_${contactField.name}`,
              `${field.id}_${contactField.id}`,
              `${field.name}_${contactField.id}`
            ], seenKeys);
            items.push({
              key: contactField.id || contactField.name,
              type: 'contact-field',
              label,
              rawValue,
              displayValue: formatValueForDisplay(rawValue),
              field: contactField,
              parentContact: field,
              matchedKey
            });
          });
          return;
        }

        const fieldMeta = resolveFieldMeta(fieldIndex, field.id || field.name) || { field };
        
        // ALWAYS use the field's label property directly from the form structure
        // Try multiple possible label properties
        let label = null;
        
        // Check all possible label properties in order of preference
        if (field.label && typeof field.label === 'string' && field.label.trim()) {
          label = field.label.trim();
        } else if (field.question && typeof field.question === 'string' && field.question.trim()) {
          label = field.question.trim();
        } else if (field.title && typeof field.title === 'string' && field.title.trim()) {
          label = field.title.trim();
        } else if (field.placeholder && typeof field.placeholder === 'string' && field.placeholder.trim()) {
          label = field.placeholder.trim();
        }
        
          // Double-check: if the label is the same as the ID or name (normalized), skip it
          if (label) {
            const labelNormalized = normalizeKey(label);
            const idNormalized = normalizeKey(String(field.id || ''));
            const nameNormalized = normalizeKey(String(field.name || ''));
            
            if (labelNormalized === idNormalized || labelNormalized === nameNormalized) {
              label = null;
            }
          }
          
          // If we still don't have a label, try to format the name (if it's different from ID)
          if (!label && field.name && field.name !== field.id) {
            const formattedName = formatKeyLabel(field.name);
            // Only use if it doesn't look like an ID
            if (!isGeneratedFieldId(formattedName) && formattedName !== 'Field') {
              label = formattedName;
            }
          }
          
          // Final fallback - use "Unnamed Field" instead of showing IDs
          if (!label) {
            label = 'Unnamed Field';
          }
        
        // Use getFieldLabelFromMeta to handle special cases (comments, repeatables, etc.)
        // but pass the label we found as the fallback
        label = getFieldLabelFromMeta(fieldMeta, label);

        if (field.type === 'matrix') {
          const rows = getMatrixRows(field);
          const cols = getMatrixCols(field);
          
          // Matrix data structure: { [rowKey]: { [colKey]: value } } or { [rowKey]: colKey } for radio
          const matrixValue = responseData[field.name] || responseData[field.id] || {};
          
          const matrixRows = rows.map((row, rowIndex) => {
            const rowKey = typeof row === 'string' ? row : row.key || row.label || `row_${rowIndex}`;
            const rowLabel = typeof row === 'string'
              ? formatKeyLabel(row)
              : formatKeyLabel(row.label || row.key || `Row ${rowIndex + 1}`);
            
            // Get the row data - could be an object (for text/number/checkbox) or a single value (for radio)
            const rowData = matrixValue[rowKey];
            
            const cells = cols.map((col, colIndex) => {
              const colKey = col.key || col.label || `col_${colIndex + 1}`;
              const colLabel = formatKeyLabel(col.label || col.key || `Column ${colIndex + 1}`);
              
              // Determine cell value based on matrix cell type
              let rawValue = null;
              const cellType = field.matrixCellType || 'radio';
              
              if (cellType === 'radio') {
                // For radio, rowData is the selected colKey
                rawValue = rowData === colKey ? colKey : null;
              } else if (cellType === 'checkbox') {
                // For checkbox, rowData is an object with colKeys as keys
                rawValue = rowData && typeof rowData === 'object' && rowData[colKey] ? colKey : null;
              } else {
                // For text/number, rowData is an object with colKeys as keys
                rawValue = rowData && typeof rowData === 'object' ? rowData[colKey] : null;
              }
              
              // Mark this key as seen
              const cellKey = `${field.name || field.id}_${rowKey}_${colKey}`;
              seenKeys.add(cellKey);
              seenKeys.add(`${field.id}_${rowKey}_${colKey}`);
              seenKeys.add(`${field.name}_${rowKey}_${colKey}`);
              
              return {
                key: `${rowKey}_${colKey}`,
                label: colLabel,
                rawValue,
                displayValue: formatValueForDisplay(rawValue),
                matchedKey: cellKey
              };
            });
            return { key: rowKey, label: rowLabel, cells };
          });

          // Mark the main field key as seen
          seenKeys.add(field.name || field.id);
          seenKeys.add(String(field.name));
          seenKeys.add(String(field.id));

          items.push({
            key: field.id || field.name,
            type: 'matrix',
            label,
            matrix: {
              rows: matrixRows,
              field
            },
            field
          });
        } else {
          // Try to match response data by field ID, name, and various formats
          // Response data might have keys like "field_1763966410378" or "q_1763966431017"
          // while form structure has IDs like "1763966410378" or "1763966431017"
          const possibleKeys = [];
          
          // Add the field ID and name in various formats
          if (field.id) {
            possibleKeys.push(field.id, String(field.id));
            // Try with prefixes
            possibleKeys.push(`field_${field.id}`, `q_${field.id}`);
          }
          if (field.name) {
            possibleKeys.push(field.name, String(field.name));
            // Try with prefixes
            possibleKeys.push(`field_${field.name}`, `q_${field.name}`);
          }
          
          // Remove duplicates and filter out falsy values
          const uniqueKeys = [...new Set(possibleKeys.filter(Boolean))];
          
          let { value: rawValue, matchedKey } = extractValueWithKey(responseData, uniqueKeys, seenKeys);

          // Heuristic fallback: if select/radio/checkbox is empty, find an unused response value that matches an option.
          if ((!rawValue || rawValue === '') && responseData && ['select', 'radio', 'checkbox'].includes(field.type) && Array.isArray(field.options) && field.options.length > 0) {
            const optionsSet = new Set(field.options.map((o) => String(o)));
            for (const [k, v] of Object.entries(responseData)) {
              if (!k || seenKeys.has(k)) continue;
              if (k.startsWith('_') || k === 'session_id') continue;

              if (typeof v === 'string' && optionsSet.has(v)) {
                seenKeys.add(k);
                rawValue = v;
                matchedKey = k;
                break;
              }
              if (field.type === 'checkbox' && Array.isArray(v) && v.every((x) => optionsSet.has(String(x)))) {
                seenKeys.add(k);
                rawValue = v;
                matchedKey = k;
                break;
              }
            }
          }

          // Map "Option N" placeholders to the current option text (helps when options were renamed after submission)
          rawValue = mapPlaceholderOptionValue(rawValue, field);
          
          items.push({
            key: field.id || field.name,
            type: 'field',
            label,
            rawValue,
            displayValue: formatValueForDisplay(rawValue),
            field,
            matchedKey
          });
        }

        if (['select', 'radio', 'checkbox'].includes(field.type) && field.allowComment) {
          const { value: commentRaw } = extractValueWithKey(responseData, [
            `${field.name}_comment`,
            `${field.id}_comment`,
            `${String(field.name)}_comment`,
            `${String(field.id)}_comment`
          ], seenKeys);
          const commentLabel = getFieldLabelFromMeta(
            { field, isComment: true },
          `${label} - ${formatKeyLabel(field.commentLabel || 'Comments')}`
          );
          items.push({
            key: `${field.id || field.name}_comment`,
            type: 'comment',
            label: commentLabel,
            rawValue: commentRaw,
            displayValue: formatValueForDisplay(commentRaw),
            field
          });
        }
      });
    };

    iterateFields(Array.isArray(formStructure) ? formStructure : []);

    Object.entries(responseData || {}).forEach(([key, value]) => {
      if (!key) return;
      if (seenKeys.has(key)) return;
      if (key.startsWith('_') || key === 'session_id') return;
      
      // Try to find the field metadata
      const meta = resolveFieldMeta(fieldIndex, key);
      
      // Check if this might be a matrix field that wasn't found in the form structure
      // Matrix fields store data as { [rowKey]: { [colKey]: value } } or { [rowKey]: colKey } for radio
      if (!meta || !meta.field) {
        // Check if value looks like a matrix structure (object with objects or strings)
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          const entries = Object.entries(value);
          
          // Check if it looks like matrix data - either:
          // 1. All values are objects (text/number/checkbox matrix)
          // 2. All values are strings (radio matrix)
          // 3. Mixed but follows matrix pattern
          const looksLikeMatrix = entries.length > 0 && entries.every(([rowKey, rowValue]) => {
            // Matrix structure: rowValue is either a string (radio) or object (text/number/checkbox)
            return typeof rowValue === 'string' || 
                   (typeof rowValue === 'object' && rowValue !== null && !Array.isArray(rowValue));
          });
          
          if (looksLikeMatrix) {
            // This is likely a matrix field - try to find it by checking all fields
            // Try multiple matching strategies
            const findMatrixField = (fields) => {
              if (!Array.isArray(fields)) return null;
              
              for (const f of fields) {
                if (!f || typeof f !== 'object') continue;
                
                // Check if this field is a matrix
                if (f.type === 'matrix') {
                  // Try multiple matching strategies
                  const normalizedKey = normalizeKey(key);
                  const fieldNameNorm = f.name ? normalizeKey(String(f.name)) : '';
                  const fieldIdNorm = f.id ? normalizeKey(String(f.id)) : '';
                  const fieldLabelNorm = f.label ? normalizeKey(String(f.label)) : '';
                  
                  if (fieldNameNorm === normalizedKey || 
                      fieldIdNorm === normalizedKey ||
                      fieldLabelNorm === normalizedKey ||
                      f.name === key ||
                      f.id === key ||
                      String(f.name) === key ||
                      String(f.id) === key ||
                      f.label === key) {
                    return f;
                  }
                }
                
                // Check nested fields (repeatable, contact, etc.)
                if (f.type === 'repeatable' && Array.isArray(f.repeatableFields)) {
                  const found = findMatrixField(f.repeatableFields);
                  if (found) return found;
                }
                if (f.type === 'contact' && Array.isArray(f.contactFields)) {
                  const found = findMatrixField(f.contactFields);
                  if (found) return found;
                }
              }
              return null;
            };
            
            const matrixField = findMatrixField(formStructure);
            
            if (matrixField) {
              // Found the matrix field - process it properly
              const rows = getMatrixRows(matrixField);
              const cols = getMatrixCols(matrixField);
              
              const matrixRows = rows.map((row, rowIndex) => {
                const rowKey = typeof row === 'string' ? row : row.key || row.label || `row_${rowIndex}`;
                const rowLabel = typeof row === 'string'
                  ? formatKeyLabel(row)
                  : formatKeyLabel(row.label || row.key || `Row ${rowIndex + 1}`);
                
                const rowData = value[rowKey];
                const cellType = matrixField.matrixCellType || 'radio';
                
                const cells = cols.map((col, colIndex) => {
                  const colKey = col.key || col.label || `col_${colIndex + 1}`;
                  const colLabel = formatKeyLabel(col.label || col.key || `Column ${colIndex + 1}`);
                  
                  let rawValue = null;
                  if (cellType === 'radio') {
                    rawValue = rowData === colKey ? colKey : null;
                  } else if (cellType === 'checkbox') {
                    rawValue = rowData && typeof rowData === 'object' && rowData[colKey] ? colKey : null;
                  } else {
                    rawValue = rowData && typeof rowData === 'object' ? rowData[colKey] : null;
                  }
                  
                  return {
                    key: `${rowKey}_${colKey}`,
                    label: colLabel,
                    rawValue,
                    displayValue: formatValueForDisplay(rawValue),
                    matchedKey: key
                  };
                });
                return { key: rowKey, label: rowLabel, cells };
              });
              
              const fieldLabel = matrixField.label || matrixField.question || matrixField.title || matrixField.name || 'Unnamed Field';
              
              items.push({
                key,
                type: 'matrix',
                label: fieldLabel,
                matrix: {
                  rows: matrixRows,
                  field: matrixField
                },
                field: matrixField
              });
              
              seenKeys.add(key);
              return; // Skip the rest of the processing
            } else {
              // Matrix field not found, but data looks like matrix
              // This might be a case where the row label is used as the key
              // Check if any of the keys match matrix row labels
              const allMatrixFields = [];
              const findAllMatrixFields = (fields) => {
                if (!Array.isArray(fields)) return;
                fields.forEach(f => {
                  if (f && f.type === 'matrix') {
                    allMatrixFields.push(f);
                  }
                  if (f && f.type === 'repeatable' && Array.isArray(f.repeatableFields)) {
                    findAllMatrixFields(f.repeatableFields);
                  }
                  if (f && f.type === 'contact' && Array.isArray(f.contactFields)) {
                    findAllMatrixFields(f.contactFields);
                  }
                });
              };
              findAllMatrixFields(formStructure);
              
              // Try to match by checking if any row label matches the key
              for (const matrixField of allMatrixFields) {
                const rows = getMatrixRows(matrixField);
                const rowKeys = rows.map((row, idx) => {
                  if (typeof row === 'string') return row;
                  return row.key || row.label || `row_${idx}`;
                });
                const rowLabels = rows.map((row, idx) => {
                  if (typeof row === 'string') return formatKeyLabel(row);
                  return formatKeyLabel(row.label || row.key || `Row ${idx + 1}`);
                });
                
                // Check if the key matches any row label or key
                const matchedRowIndex = rowKeys.findIndex(rk => 
                  normalizeKey(rk) === normalizeKey(key) || rk === key
                );
                const matchedLabelIndex = rowLabels.findIndex(rl => 
                  normalizeKey(rl) === normalizeKey(key) || rl === key
                );
                
                if (matchedRowIndex >= 0 || matchedLabelIndex >= 0) {
                  // Found a match - this key is a row, and the value contains column data
                  // The actual field name should be stored elsewhere, but we can still display it
                  const rowIndex = matchedRowIndex >= 0 ? matchedRowIndex : matchedLabelIndex;
                  const row = rows[rowIndex];
                  const rowKey = typeof row === 'string' ? row : row.key || row.label || `row_${rowIndex}`;
                  const rowLabel = typeof row === 'string' ? formatKeyLabel(row) : formatKeyLabel(row.label || row.key || `Row ${rowIndex + 1}`);
                  
                  const cols = getMatrixCols(matrixField);
                  const cellType = matrixField.matrixCellType || 'radio';
                  
                  // The value itself is the row data (column values)
                  const rowData = value;
                  
                  const cells = cols.map((col, colIndex) => {
                    const colKey = col.key || col.label || `col_${colIndex + 1}`;
                    const colLabel = formatKeyLabel(col.label || col.key || `Column ${colIndex + 1}`);
                    
                    let rawValue = null;
                    if (cellType === 'radio') {
                      rawValue = rowData === colKey ? colKey : null;
                    } else if (cellType === 'checkbox') {
                      rawValue = rowData && typeof rowData === 'object' && rowData[colKey] ? colKey : null;
                    } else {
                      rawValue = rowData && typeof rowData === 'object' ? rowData[colKey] : null;
                    }
                    
                    return {
                      key: `${rowKey}_${colKey}`,
                      label: colLabel,
                      rawValue,
                      displayValue: formatValueForDisplay(rawValue),
                      matchedKey: key
                    };
                  });
                  
                  const fieldLabel = matrixField.label || matrixField.question || matrixField.title || matrixField.name || 'Unnamed Field';
                  
                  items.push({
                    key,
                    type: 'matrix',
                    label: `${fieldLabel} - ${rowLabel}`,
                    matrix: {
                      rows: [{ key: rowKey, label: rowLabel, cells }],
                      field: matrixField
                    },
                    field: matrixField
                  });
                  
                  seenKeys.add(key);
                  return; // Skip the rest of the processing
                }
              }
            }
          }
        }
      }
      
      // If we found metadata with a field, use its label
      let label;
      if (meta && meta.field) {
        // Use the field's label from the form structure
        const fieldLabel = meta.field.label || meta.field.question || meta.field.title || meta.field.placeholder;
        if (fieldLabel && fieldLabel !== meta.field.id && fieldLabel !== meta.field.name && fieldLabel !== key) {
          label = getFieldLabelFromMeta(meta, fieldLabel);
        } else {
          // Field doesn't have a proper label, use "Unnamed Field"
          label = 'Unnamed Field';
        }
      } else {
        // No metadata found - this shouldn't happen if form structure is correct
        // But if it does, use "Unnamed Field" instead of formatting the key
        label = 'Unnamed Field';
      }
      
      items.push({
        key,
        type: 'unknown',
        label,
        rawValue: value,
        displayValue: formatValueForDisplay(value),
        field: meta?.field
      });
    });

    return items;
  };


  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to download file
  const downloadFile = async (filenameOrBlob, filename, mimeType) => {
    try {
      const link = document.createElement('a');
      let url;
      let downloadName;

      if (filenameOrBlob instanceof Blob) {
        // Called with blob, filename, mimeType
        url = URL.createObjectURL(filenameOrBlob);
        downloadName = filename;
      } else {
        // Called with just filename (string) - download from backend
        const fileUrl = getFileURL(filenameOrBlob, { download: true });
        
        try {
          // Try to fetch the file from the backend
          const response = await fetch(fileUrl);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const blob = await response.blob();
          url = URL.createObjectURL(blob);
          downloadName = filenameOrBlob;
        } catch (fetchError) {
          console.error('Failed to fetch file from backend:', fetchError);
          // Fallback to placeholder file
          const fileContent = `This is a sample file: ${filenameOrBlob}\n\nGenerated on: ${new Date().toLocaleString()}\n\nThis is a placeholder for the actual file content.\n\nNote: The actual file could not be retrieved from the server.`;
          const blob = new Blob([fileContent], { type: 'text/plain' });
          url = URL.createObjectURL(blob);
          downloadName = filenameOrBlob;
          toast.warning('Using placeholder file - actual file could not be retrieved');
        }
      }
      
      link.href = url;
      link.download = downloadName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL
      URL.revokeObjectURL(url);
      
      toast.success(`Download started for ${downloadName}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  // API base for uploaded files (same as other API calls)
  const getResponsesApiBase = () => {
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5000/api';
    }
    const baseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    return `${baseUrl}/api`;
  };

  /** Direct file URL; use download: true so Save As uses attachment disposition when supported */
  const getFileURL = (filename, { download = false } = {}) => {
    const apiBaseUrl = getResponsesApiBase();
    let url = `${apiBaseUrl}/upload/files/${encodeURIComponent(filename)}`;
    if (download) url += '?download=1';
    return url;
  };

  /** Opens server HTML page: image/pdf preview + Download button */
  const getFileViewURL = (filename) => {
    const apiBaseUrl = getResponsesApiBase();
    return `${apiBaseUrl}/upload/files/${encodeURIComponent(filename)}/view`;
  };
  
  const [form, setForm] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResponses, setTotalResponses] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  
  // Sorting and filtering state
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [viewTracking, setViewTracking] = useState(null);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [activeTab, setActiveTab] = useState('responses');
  const [certificateDownloads, setCertificateDownloads] = useState([]);
  const [loadingCertificates, setLoadingCertificates] = useState(false);
  const [certificateStartDate, setCertificateStartDate] = useState('');
  const [certificateEndDate, setCertificateEndDate] = useState('');
  const [selectedCertificates, setSelectedCertificates] = useState(new Set());
  const [downloadingCertificates, setDownloadingCertificates] = useState(false);
  
  const fieldIndex = useMemo(() => {
    if (!form || !form.form_structure) return new Map();
    
    // Handle questionnaire structure (has formFields and questions)
    let structure = [];
    if (typeof form.form_structure === 'object' && !Array.isArray(form.form_structure)) {
      // It's a questionnaire or object structure
      if (form.form_structure.formFields && Array.isArray(form.form_structure.formFields)) {
        structure = [...form.form_structure.formFields];
      }
      if (form.form_structure.questions && Array.isArray(form.form_structure.questions)) {
        structure = [...structure, ...form.form_structure.questions];
      }
      // Also check if there's a direct fields array
      if (form.form_structure.fields && Array.isArray(form.form_structure.fields)) {
        structure = [...structure, ...form.form_structure.fields];
      }
    } else if (Array.isArray(form.form_structure)) {
      structure = form.form_structure;
    }
    
    return buildFieldIndex(structure);
  }, [form]);
  
  const displayFields = useMemo(() => {
    if (!selectedResponse || !form) return [];
    
    // Handle questionnaire structure (has formFields and questions)
    let structure = [];
    if (form.form_structure && typeof form.form_structure === 'object' && !Array.isArray(form.form_structure)) {
      // It's a questionnaire or object structure
      if (form.form_structure.formFields && Array.isArray(form.form_structure.formFields)) {
        structure = [...form.form_structure.formFields];
      }
      if (form.form_structure.questions && Array.isArray(form.form_structure.questions)) {
        structure = [...structure, ...form.form_structure.questions];
      }
      // Also check if there's a direct fields array
      if (form.form_structure.fields && Array.isArray(form.form_structure.fields)) {
        structure = [...structure, ...form.form_structure.fields];
      }
    } else if (Array.isArray(form.form_structure)) {
      structure = form.form_structure;
    }
    
    const fields = buildDisplayFields(structure, selectedResponse.response_data || {}, fieldIndex);
    
    return fields;
  }, [form, selectedResponse, fieldIndex]);
  
  // Settings state
  const [settings, setSettings] = useState({
    expiryEnabled: false,
    expiryDate: '',
    expiryTime: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const tabs = [
    {
      id: 'responses',
      label: 'Responses',
      icon: <FiUsers />,
      count: totalResponses
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: <FiBarChart2 />,
      count: viewTracking ? viewTracking.total_views : 0
    },
    {
      id: 'form-info',
      label: 'Form Details',
      icon: <FiFileText />,
      count: form?.form_structure?.length || 0
    },
    {
      id: 'export',
      label: 'Export Data',
      icon: <FiDownload />,
      count: responses.length
    },
    // Only show Certificates tab for questionnaires
    ...(form && isQuestionnaire(form) ? [{
      id: 'certificates',
      label: 'Certificates',
      icon: <FiAward />,
      count: certificateDownloads.length
    }] : []),
    {
      id: 'settings',
      label: 'Settings',
      icon: <FiSettings />,
      count: null
    }
  ];

  useEffect(() => {
    loadData();
    loadViewTracking();
    loadSettings();
  }, [formId, currentPage, pageSize]);

  useEffect(() => {
    if (activeTab === 'certificates' && form && isQuestionnaire(form)) {
      loadCertificateDownloads();
    }
  }, [activeTab, form, formId]);

  // Check authentication status on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    console.log('🔍 Responses component mounted');
    console.log('🔑 Token in localStorage:', token ? 'EXISTS' : 'MISSING');
    if (token) {
      console.log('🔑 Token preview:', token.substring(0, 20) + '...');
    }
  }, []);

  const loadCertificateDownloads = async () => {
    try {
      setLoadingCertificates(true);
      const response = await certificatesAPI.getCertificateDownloads(formId);
      if (response.data.success) {
        setCertificateDownloads(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load certificate downloads:', error);
      toast.error('Failed to load certificate downloads');
    } finally {
      setLoadingCertificates(false);
    }
  };

  const loadViewTracking = async () => {
    try {
      console.log('🔍 Starting analytics load...');
      console.log('📋 Form ID:', formId);
      console.log('🔑 Token exists:', !!localStorage.getItem('token'));
      console.log('🌐 API Base URL:', process.env.REACT_APP_API_URL || 'http://localhost:5000/api');
      
      setAnalyticsError(null);
      
      console.log('📡 Making API call to getViewTracking...');
      const response = await responsesAPI.getViewTracking(formId);
      
      console.log('✅ API Response received:', response);
      console.log('📊 Response data:', response.data);
      console.log('📈 Analytics data:', response.data.data);
      
      setViewTracking(response.data.data);
      console.log('✅ Analytics data set successfully');
    } catch (error) {
      console.error('❌ Failed to load view tracking data:', error);
      console.error('🔍 Error response:', error.response);
      console.error('📝 Error data:', error.response?.data);
      console.error('📄 Error message:', error.message);
      console.error('🔗 Error config:', error.config);
      
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load analytics data';
      console.log('⚠️ Setting error message:', errorMessage);
      setAnalyticsError(errorMessage);
    }
  };

  const openResponseModal = (response) => {
    console.log('🔍 Opening response modal with response:', response);
    console.log('📋 Response response_data:', response.response_data);
    console.log('📋 Response response_data type:', typeof response.response_data);
    console.log('📋 Response keys:', Object.keys(response));
    
    // Parse response_data if it's a string
    const processedResponse = { ...response };
    if (typeof response.response_data === 'string') {
      try {
        processedResponse.response_data = JSON.parse(response.response_data);
        console.log('📋 Parsed response_data:', processedResponse.response_data);
      } catch (e) {
        console.error('📋 Failed to parse response_data:', e);
      }
    }
    
    setSelectedResponse(processedResponse);
  };

  const deleteResponse = async (responseId) => {
    if (!window.confirm('Are you sure you want to delete this response?')) {
      return;
    }

    try {
      await responsesAPI.delete(responseId);
      setResponses(prev => prev.filter(r => r.id !== responseId));
      toast.success('Response deleted successfully');
    } catch (error) {
      console.error('Failed to delete response:', error);
      toast.error('Failed to delete response');
    }
  };

  const loadSettings = async () => {
    try {
      const response = await formsAPI.get(formId);
      if (response.data.success) {
        const formData = response.data.data;
        setSettings({
          expiryEnabled: !!formData.expiry_date,
          expiryDate: formData.expiry_date ? formData.expiry_date.split('T')[0] : '',
          expiryTime: formData.expiry_date ? formData.expiry_date.split('T')[1]?.substring(0, 5) : ''
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleSettingsChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      
      const expiryDateTime = settings.expiryEnabled && settings.expiryDate && settings.expiryTime
        ? `${settings.expiryDate}T${settings.expiryTime}:00.000Z`
        : null;

      const updateData = {
        expiry_date: expiryDateTime
      };

      await formsAPI.update(formId, updateData);
      toast.success('Settings saved successfully');
      
      // Reload form data to get updated expiry
      loadData();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const isFormExpired = () => {
    if (!form?.expiry_date) return false;
    return new Date() > new Date(form.expiry_date);
  };

  const getExpiryStatus = () => {
    // Check if form is manually deactivated
    if (!form?.is_active) {
      return { status: 'inactive', message: 'Form is manually deactivated' };
    }
    
    // Check if form has expired
    if (form?.expiry_date) {
      const now = new Date();
      const expiry = new Date(form.expiry_date);
      const isExpired = now > expiry;
      
      if (isExpired) {
        return { status: 'expired', message: `Form expired on ${expiry.toLocaleString()}` };
      } else {
        const timeLeft = expiry - now;
        const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        
        return { 
          status: 'active', 
          message: `Form expires in ${days} days, ${hours} hours` 
        };
      }
    }
    
    // No expiry set and form is active
    return { status: 'active', message: 'No expiry set - form is always active' };
  };

  const handleReactivateForm = async () => {
    if (!window.confirm('Are you sure you want to reactivate this form? This will remove the expiry date and make it always active.')) {
      return;
    }

    try {
      setSavingSettings(true);
      
      const updateData = {
        expiry_date: null
      };

      await formsAPI.update(formId, updateData);
      toast.success('Form reactivated successfully!');
      
      // Reload form data to get updated expiry
      loadData();
      loadSettings();
    } catch (error) {
      console.error('Error reactivating form:', error);
      toast.error('Failed to reactivate form');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleExtendExpiry = async () => {
    // Show a prompt for new expiry date
    const newExpiryDate = prompt('Enter new expiry date (YYYY-MM-DD):');
    if (!newExpiryDate) return;

    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(newExpiryDate)) {
      toast.error('Please enter a valid date in YYYY-MM-DD format');
      return;
    }

    // Check if date is in the future
    const selectedDate = new Date(newExpiryDate);
    const now = new Date();
    if (selectedDate <= now) {
      toast.error('Expiry date must be in the future');
      return;
    }

    try {
      setSavingSettings(true);
      
      // Set expiry time to end of day (23:59:59)
      const expiryDateTime = `${newExpiryDate}T23:59:59.000Z`;

      const updateData = {
        expiry_date: expiryDateTime
      };

      await formsAPI.update(formId, updateData);
      toast.success('Form expiry extended successfully!');
      
      // Reload form data to get updated expiry
      loadData();
      loadSettings();
    } catch (error) {
      console.error('Error extending expiry:', error);
      toast.error('Failed to extend expiry');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleToggleActive = async (isActive) => {
    try {
      setSavingSettings(true);
      
      const updateData = {
        is_active: isActive
      };

      await formsAPI.update(formId, updateData);
      
      const statusText = isActive ? 'activated' : 'deactivated';
      toast.success(`Form ${statusText} successfully!`);
      
      // Reload form data to get updated status
      loadData();
    } catch (error) {
      console.error('Error toggling form status:', error);
      toast.error('Failed to update form status');
    } finally {
      setSavingSettings(false);
    }
  };

  const exportData = async (format) => {
    try {
      setIsExportDropdownOpen(false);
      toast.info(`Exporting data as ${format.toUpperCase()}...`);
      
      // Prepare date filters for export
      const filters = {};
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      
      let response;
      let mimeType;
      let filename;

      switch (format) {
        case 'csv':
          response = await responsesAPI.exportCSV(formId, filters);
          mimeType = 'text/csv';
          filename = `${form?.title || 'questionnaire'}_responses.csv`;
          break;
        case 'json':
          response = await responsesAPI.exportJSON(formId, filters);
          mimeType = 'application/json';
          filename = `${form?.title || 'questionnaire'}_responses.json`;
          break;
        case 'excel':
          response = await responsesAPI.exportExcel(formId, filters);
          mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          filename = `${form?.title || 'questionnaire'}_responses.xlsx`;
          break;
        case 'pdf':
          response = await responsesAPI.exportPDF(formId, filters);
          mimeType = 'application/pdf';
          filename = `${form?.title || 'questionnaire'}_responses.pdf`;
          break;
        default:
          throw new Error('Unsupported export format');
      }
      
      // Create download link
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Data exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      console.error('Failed to export data:', error);
      console.error('Error details:', error.response?.data);
      toast.error(`Failed to export data: ${error.message}`);
    }
  };

  const renderResponsesTab = () => (
    <>
      {responses.length === 0 ? (
        <EmptyState>
          <EmptyStateTitle>No responses yet</EmptyStateTitle>
          <EmptyStateDescription>
            Responses will appear here once users start submitting the form.
          </EmptyStateDescription>
        </EmptyState>
      ) : (
        <>
          <FilterControls>
            <FilterSection>
              <FilterLabel>
                <FiFilter />
                Sort & Filter
              </FilterLabel>
              <SortDropdown className="sort-dropdown">
                <SortButton onClick={() => setIsSortMenuOpen(!isSortMenuOpen)}>
                  <FiFilter />
                  {sortBy === 'date' ? 'Date' : 'ID'}
                  {sortOrder === 'asc' ? <FiArrowUp /> : <FiArrowDown />}
                  <FiChevronDown />
                </SortButton>
                <SortMenu isOpen={isSortMenuOpen}>
                  <SortOption onClick={() => handleSortChange('date')}>
                    <FiCalendar />
                    Date {sortBy === 'date' && (sortOrder === 'asc' ? <FiArrowUp /> : <FiArrowDown />)}
                  </SortOption>
                  <SortOption onClick={() => handleSortChange('id')}>
                    <FiUsers />
                    ID {sortBy === 'id' && (sortOrder === 'asc' ? <FiArrowUp /> : <FiArrowDown />)}
                  </SortOption>
                </SortMenu>
              </SortDropdown>
            </FilterSection>

            <FilterSection>
              <FilterLabel>
                <FiCalendar />
                Date Range
              </FilterLabel>
              <DateRangeContainer>
                <DateInput
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  placeholder="Start date"
                  title="Filter from this date"
                />
                <DateSeparator>to</DateSeparator>
                <DateInput
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  placeholder="End date"
                  title="Filter until this date"
                />
              </DateRangeContainer>
            </FilterSection>

            {hasActiveFilters && (
              <FilterSection>
                <ClearFiltersButton onClick={clearFilters}>
                  <FiX />
                  Clear All
                </ClearFiltersButton>
              </FilterSection>
            )}

            {hasActiveFilters && (
              <ActiveFilters>
                <ActiveFiltersLabel>Active Filters:</ActiveFiltersLabel>
                {startDate && (
                  <FilterTag>
                    <FiCalendar />
                    From: {formatKuwaitDate(startDate)}
                    <RemoveFilterButton onClick={() => setStartDate('')}>
                      <FiX size={12} />
                    </RemoveFilterButton>
                  </FilterTag>
                )}
                {endDate && (
                  <FilterTag>
                    <FiCalendar />
                    Until: {formatKuwaitDate(endDate)}
                    <RemoveFilterButton onClick={() => setStartDate('')}>
                      <FiX size={12} />
                    </RemoveFilterButton>
                  </FilterTag>
                )}
                <FilterTag>
                  <FiFilter />
                  Sort: {sortBy} ({sortOrder})
                  <RemoveFilterButton onClick={() => { setSortBy('date'); setSortOrder('desc'); }}>
                    <FiX size={12} />
                  </RemoveFilterButton>
                </FilterTag>
              </ActiveFilters>
            )}
          </FilterControls>

          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>ID</TableHeaderCell>
                <TableHeaderCell>Submitted</TableHeaderCell>
                {isQuestionnaire(form) && <TableHeaderCell>Score</TableHeaderCell>}
                <TableHeaderCell>Preview</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHead>
            <tbody>
              {responses.map((response) => {
                const responseData = response.response_data || {};
                const hasScore = responseData._questionnaire_percentage !== undefined;
                
                return (
                  <TableRow key={response.id}>
                    <TableCell>
                      <ResponseId>{formatResponseId(response.id)}</ResponseId>
                    </TableCell>
                    <TableCell>
                      <DateCell>
                        {formatKuwaitDate(response.submitted_at)}
                        <TimeCell>
                          {formatKuwaitTime(response.submitted_at)}
                        </TimeCell>
                      </DateCell>
                    </TableCell>
                    {isQuestionnaire(form) && (
                      <TableCell>
                        {hasScore ? (
                          <ScoreBadge percentage={responseData._questionnaire_percentage}>
                            <FiAward />
                            {responseData._questionnaire_percentage?.toFixed(1)}%
                            <span style={{ opacity: 0.9, fontSize: '11px', marginLeft: '4px' }}>
                              ({getRemark(responseData._questionnaire_percentage)})
                            </span>
                          </ScoreBadge>
                        ) : (
                          <span style={{ color: '#6b7280', fontSize: '13px' }}>No score</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <PreviewButton onClick={() => openResponseModal(response)}>
                        <FiEye />
                        View
                      </PreviewButton>
                    </TableCell>
                    <TableCell>
                      <ActionButtonStyled
                        variant="danger"
                        onClick={() => deleteResponse(response.id)}
                      >
                        <FiTrash2 />
                        Delete
                      </ActionButtonStyled>
                    </TableCell>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>

          {totalResponses > 0 && (
            <Pagination>
              <PaginationInfo>
                <PageInfo>
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalResponses)} of {totalResponses} responses
                </PageInfo>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>Per page:</label>
                  <PageSizeSelector
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing page size
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </PageSizeSelector>
                </div>
              </PaginationInfo>
              
              {totalResponses > pageSize && (
                <PaginationButtons>
                  <PaginationButton
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    title="First page"
                  >
                    First
                  </PaginationButton>
                  
                  <PaginationButton
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    title="Previous page"
                  >
                    <FiChevronLeft />
                    Previous
                  </PaginationButton>
                  
                  {/* Page number buttons */}
                  {totalPages > 1 && (() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                    
                    // Adjust start page if we're near the end
                    if (endPage - startPage < maxVisiblePages - 1) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }
                    
                    // First page
                    if (startPage > 1) {
                      pages.push(
                        <PageButton
                          key={1}
                          onClick={() => setCurrentPage(1)}
                          active={currentPage === 1}
                        >
                          1
                        </PageButton>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <span key="ellipsis1" style={{ padding: '0 4px', color: '#9ca3af' }}>...</span>
                        );
                      }
                    }
                    
                    // Visible page range
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <PageButton
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          active={currentPage === i}
                        >
                          {i}
                        </PageButton>
                      );
                    }
                    
                    // Last page
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="ellipsis2" style={{ padding: '0 4px', color: '#9ca3af' }}>...</span>
                        );
                      }
                      pages.push(
                        <PageButton
                          key={totalPages}
                          onClick={() => setCurrentPage(totalPages)}
                          active={currentPage === totalPages}
                        >
                          {totalPages}
                        </PageButton>
                      );
                    }
                    
                    return pages;
                  })()}
                  
                  <PaginationButton
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                    title="Next page"
                  >
                    Next
                    <FiChevronRight />
                  </PaginationButton>
                  
                  <PaginationButton
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage >= totalPages}
                    title="Last page"
                  >
                    Last
                  </PaginationButton>
                </PaginationButtons>
              )}
            </Pagination>
          )}
        </>
      )}
    </>
  );

  const renderAnalyticsTab = () => (
    <div>
      <h3 style={{ marginBottom: '24px', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
        View Analytics
      </h3>
      {analyticsError ? (
        <div style={{
          padding: '16px',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          backgroundColor: '#fef2f2',
          color: '#dc2626',
          marginBottom: '24px'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>⚠️ Analytics Error</div>
          <div style={{ fontSize: '12px' }}>{analyticsError}</div>
          <button
            onClick={loadViewTracking}
            style={{
              marginTop: '12px',
              padding: '6px 14px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <StatsGrid>
            <StatCard color="#10b981">
              <StatValue>{totalResponses}</StatValue>
              <StatLabel>Total Responses</StatLabel>
            </StatCard>
            <StatCard color="#8b5cf6">
              <StatValue>
                {viewTracking ? viewTracking.total_views : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AnalyticsSpinner color="#8b5cf6" />
                    <span>Loading...</span>
                  </div>
                )}
              </StatValue>
              <StatLabel>
                {viewTracking ? 'Total Views' : 'View Tracking'}
              </StatLabel>
            </StatCard>
            {viewTracking && (
              <StatCard color="#06b6d4">
                <StatValue>{viewTracking.unique_visitors}</StatValue>
                <StatLabel>Unique Visitors</StatLabel>
              </StatCard>
            )}
            {viewTracking && (
              <StatCard color="#84cc16">
                <StatValue>{viewTracking.recent_views}</StatValue>
                <StatLabel>Views (7 days)</StatLabel>
              </StatCard>
            )}
          </StatsGrid>

          {viewTracking && viewTracking.daily_views && viewTracking.daily_views.length > 0 && (
            <div style={{ 
              background: 'rgba(249, 250, 251, 0.5)', 
              padding: '20px', 
              borderRadius: '12px',
              marginTop: '24px'
            }}>
              <h4 style={{ marginBottom: '14px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                Daily View Activity (Last 7 Days)
              </h4>
              <div style={{ display: 'grid', gap: '8px' }}>
                {viewTracking.daily_views.map((day, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 14px',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}>
                    <span style={{ fontWeight: '500', color: '#374151', fontSize: '12px' }}>
                      {formatKuwaitDate(day.date)}
                    </span>
                    <span style={{ 
                      background: 'black', 
                      color: 'white', 
                      padding: '4px 10px', 
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {day.views} views
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </>
      )}
    </div>
  );

  const renderFormInfoTab = () => (
    <div>
      <h3 style={{ marginBottom: '24px', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
        Form Details
      </h3>
      
      <div style={{ 
        background: 'rgba(249, 250, 251, 0.5)', 
        padding: '24px', 
        borderRadius: '12px', 
        marginBottom: '24px' 
      }}>
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#374151', fontSize: '15px' }}>Title:</strong>
          <div style={{ color: '#1f2937', fontSize: '12px', fontWeight: '600', marginTop: '4px' }}>
            {form.title}
          </div>
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#374151', fontSize: '15px' }}>Description:</strong>
          <div style={{ color: '#6b7280', marginTop: '4px', lineHeight: '1.5', fontSize: '12px' }}>
            {form.description || 'No description provided'}
          </div>
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#374151', fontSize: '15px' }}>Created:</strong>
          <div style={{ color: '#6b7280', marginTop: '4px', fontSize: '12px' }}>
            {form.created_at ? new Date(form.created_at).toLocaleDateString() : 'Unknown'}
          </div>
        </div>
        
        <div>
          <strong style={{ color: '#374151', fontSize: '15px' }}>Form ID:</strong>
          <div style={{ 
            color: '#6b7280', 
            marginTop: '4px', 
            fontFamily: 'monospace', 
            fontSize: '12px',
            background: 'rgba(0, 0, 0, 0.05)',
            padding: '8px',
            borderRadius: '6px',
            wordBreak: 'break-all'
          }}>
            {form.id}
          </div>
        </div>
      </div>

      {isQuestionnaire(form) ? (
        <>
          <h4 style={{ marginBottom: '16px', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
            Questionnaire Structure
          </h4>
          
          {/* Form Fields */}
          {form.form_structure?.formFields && form.form_structure.formFields.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h5 style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '12px' }}>
                Form Fields ({form.form_structure.formFields.length})
              </h5>
              <div style={{ display: 'grid', gap: '12px' }}>
                {form.form_structure.formFields.map((field, index) => (
                  <div key={field.id} style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '12px' }}>
                        {field.label || `Field ${index + 1}`}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'capitalize' }}>
                        {field.type} {field.required && '• Required'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Questions */}
          {form.form_structure?.questions && form.form_structure.questions.length > 0 ? (
            <div>
              <h5 style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '12px' }}>
                Questions ({form.form_structure.questions.length}) - {form.form_structure.totalPoints || 0} points
              </h5>
              <div style={{ display: 'grid', gap: '12px' }}>
                {form.form_structure.questions.map((question, index) => (
                  <div key={question.id} style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px'
                  }}>
                    <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '12px', marginBottom: '8px' }}>
                      Question {index + 1}: {question.question}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Type: {question.type.replace('_', ' ')} • {question.points} point{question.points !== 1 ? 's' : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#6b7280',
              background: 'rgba(249, 250, 251, 0.5)',
              borderRadius: '12px'
            }}>
              No questions defined in this questionnaire
            </div>
          )}
        </>
      ) : (
        <>
          <h4 style={{ marginBottom: '16px', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
            Form Structure ({form.form_structure?.length || 0} fields)
          </h4>
          
          {form.form_structure && Array.isArray(form.form_structure) && form.form_structure.length > 0 ? (
            <div style={{ display: 'grid', gap: '12px' }}>
              {form.form_structure.map((field, index) => (
                <div key={field.id} style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '12px' }}>
                      {field.label || `Field ${index + 1}`}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'capitalize' }}>
                      {field.type} {field.required && '• Required'}
                    </div>
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '12px' }}>
                    #{index + 1}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#6b7280',
              background: 'rgba(249, 250, 251, 0.5)',
              borderRadius: '12px'
            }}>
              No fields defined in this form
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderExportTab = () => (
    <div>
      <h3 style={{ marginBottom: '24px', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
        Export Data
      </h3>
      
      <div style={{ 
        background: 'rgba(249, 250, 251, 0.5)', 
        padding: '24px', 
        borderRadius: '12px',
        marginBottom: '24px'
      }}>
        <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '12px' }}>
          Export your form responses in various formats for analysis or backup.
        </p>
        
        <div style={{ display: 'grid', gap: '16px' }}>
          <ExportDropdown className="export-dropdown">
            <ExportButton onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}>
              <FiDownload />
              Export Data
              <FiChevronDown />
            </ExportButton>
            
            <DropdownMenu isOpen={isExportDropdownOpen}>
              <DropdownItem onClick={() => exportData('csv')}>
                <FiDownload />
                Export as CSV
              </DropdownItem>
              <DropdownItem onClick={() => exportData('json')}>
                <FiDownload />
                Export as JSON
              </DropdownItem>
              <DropdownItem onClick={() => exportData('excel')}>
                <FiDownload />
                Export as Excel
              </DropdownItem>
            </DropdownMenu>
          </ExportDropdown>
        </div>
      </div>

      <div style={{ 
        background: 'rgba(59, 130, 246, 0.05)', 
        border: '1px solid rgba(59, 130, 246, 0.1)',
        padding: '20px', 
        borderRadius: '12px' 
      }}>
        
      </div>
    </div>
  );

  const renderCertificatesTab = () => {
    // Filter to show only PDF downloads (exclude HTML views)
    let pdfDownloads = certificateDownloads.filter(download => download.download_type === 'pdf');
    
    // Apply date filtering
    if (certificateStartDate || certificateEndDate) {
      pdfDownloads = pdfDownloads.filter(download => {
        const downloadDate = new Date(download.downloaded_at);
        const downloadDateOnly = downloadDate.toISOString().split('T')[0];
        
        if (certificateStartDate && downloadDateOnly < certificateStartDate) {
          return false;
        }
        if (certificateEndDate && downloadDateOnly > certificateEndDate) {
          return false;
        }
        return true;
      });
    }
    
    const hasCertificateFilters = certificateStartDate || certificateEndDate;
    const clearCertificateFilters = () => {
      setCertificateStartDate('');
      setCertificateEndDate('');
      setSelectedCertificates(new Set());
    };
    
    const handleSelectAll = (checked) => {
      if (checked) {
        setSelectedCertificates(new Set(pdfDownloads.map(d => d.certificate_id)));
      } else {
        setSelectedCertificates(new Set());
      }
    };
    
    const handleSelectCertificate = (certificateId, checked) => {
      const newSelected = new Set(selectedCertificates);
      if (checked) {
        newSelected.add(certificateId);
      } else {
        newSelected.delete(certificateId);
      }
      setSelectedCertificates(newSelected);
    };
    
    const handleBulkDownload = async () => {
      if (selectedCertificates.size === 0) {
        toast.error('Please select at least one certificate to download');
        return;
      }
      
      try {
        setDownloadingCertificates(true);
        toast.info(`Preparing ${selectedCertificates.size} certificate(s) for download...`, { autoClose: 2000 });
        
        const zip = new JSZip();
        const certificateIds = Array.from(selectedCertificates);
        let successCount = 0;
        let failCount = 0;
        const failedCertificates = [];
        
        // Fetch all certificates and add them to the ZIP
        for (const certificateId of certificateIds) {
          try {
            const response = await certificatesAPI.getCertificatePDF(certificateId);
            
            // Check if response is actually a PDF blob
            if (!response.data) {
              throw new Error('No data received from server');
            }
            
            // Check if response is an error (JSON response instead of blob)
            if (response.data instanceof Blob) {
              if (response.data.type === 'application/json' || response.data.size < 100) {
                const errorText = await response.data.text();
                try {
                  const errorData = JSON.parse(errorText);
                  throw new Error(errorData.error || 'Failed to generate certificate PDF');
                } catch (parseError) {
                  if (response.data.size < 100) {
                    throw new Error('Failed to generate certificate PDF. Please try again.');
                  }
                  throw new Error('Failed to generate certificate PDF');
                }
              }
              
              // Verify it's a PDF by checking the first bytes
              const firstBytes = await response.data.slice(0, 4).arrayBuffer();
              const pdfHeader = new Uint8Array(firstBytes);
              const isPdf = pdfHeader[0] === 0x25 && pdfHeader[1] === 0x50 && pdfHeader[2] === 0x44 && pdfHeader[3] === 0x46;
              
              if (!isPdf) {
                const text = await response.data.text();
                if (text.includes('error') || text.includes('Error') || text.includes('failed')) {
                  try {
                    const errorData = JSON.parse(text);
                    throw new Error(errorData.error || 'Failed to generate certificate PDF');
                  } catch {
                    throw new Error('Failed to generate certificate PDF. Server returned an error.');
                  }
                }
                throw new Error('Invalid PDF file received');
              }
            }
            
            // Find the certificate data to get candidate name
            const certData = pdfDownloads.find(d => d.certificate_id === certificateId);
            const candidateName = certData?.candidate_name || 'Certificate';
            
            // Sanitize filename to avoid issues with special characters
            const sanitizedName = candidateName
              .replace(/[^a-z0-9_\-]/gi, '_')
              .replace(/_+/g, '_')
              .replace(/^_+|_+$/g, '')
              || 'Certificate';
            
            // Add PDF to ZIP
            const pdfArrayBuffer = await response.data.arrayBuffer();
            zip.file(`${sanitizedName}.pdf`, pdfArrayBuffer);
            successCount++;
          } catch (error) {
            console.error(`Error downloading certificate ${certificateId}:`, error);
            failCount++;
            const certData = pdfDownloads.find(d => d.certificate_id === certificateId);
            failedCertificates.push(certData?.candidate_name || certificateId);
          }
        }
        
        if (successCount === 0) {
          toast.error('Failed to download any certificates. Please try again.');
          setDownloadingCertificates(false);
          return;
        }
        
        // Generate ZIP file
        toast.info('Creating ZIP file...', { autoClose: 1500 });
        const zipBlob = await zip.generateAsync({ 
          type: 'blob',
          compression: 'DEFLATE',
          compressionOptions: { level: 6 }
        });
        
        // Download the ZIP file
        const formTitle = form?.title?.replace(/[^a-z0-9_\-]/gi, '_').replace(/_+/g, '_') || 'Certificates';
        const zipFileName = `${formTitle}_Certificates_${new Date().toISOString().split('T')[0]}.zip`;
        
        const url = window.URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = zipFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        if (failCount === 0) {
          toast.success(`Successfully downloaded ${successCount} certificate(s) as ZIP file!`);
        } else {
          toast.warning(`Downloaded ${successCount} certificate(s) in ZIP, ${failCount} failed. Failed: ${failedCertificates.join(', ')}`);
        }
        
        // Clear selection after download
        setSelectedCertificates(new Set());
      } catch (error) {
        console.error('Bulk download error:', error);
        toast.error('Failed to create ZIP file: ' + error.message);
      } finally {
        setDownloadingCertificates(false);
      }
    };
    
    const allSelected = pdfDownloads.length > 0 && pdfDownloads.every(d => selectedCertificates.has(d.certificate_id));
    const someSelected = selectedCertificates.size > 0 && !allSelected;
    
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
            Certificate Downloads
          </h3>
          {selectedCertificates.size > 0 && (
            <ActionButton
              onClick={handleBulkDownload}
              disabled={downloadingCertificates}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none'
              }}
            >
              <FiDownload />
              {downloadingCertificates ? `Downloading... (${selectedCertificates.size})` : `Download Selected (${selectedCertificates.size})`}
            </ActionButton>
          )}
        </div>
        
        {/* Date Filter Controls */}
        <FilterControls style={{ marginBottom: '24px' }}>
          <FilterSection>
            <FilterLabel>
              <FiCalendar />
              Date Range
            </FilterLabel>
            <DateRangeContainer>
              <DateInput
                type="date"
                value={certificateStartDate}
                onChange={(e) => setCertificateStartDate(e.target.value)}
                placeholder="Start date"
                title="Filter from this date"
              />
              <DateSeparator>to</DateSeparator>
              <DateInput
                type="date"
                value={certificateEndDate}
                onChange={(e) => setCertificateEndDate(e.target.value)}
                placeholder="End date"
                title="Filter until this date"
              />
            </DateRangeContainer>
          </FilterSection>
          
          {hasCertificateFilters && (
            <FilterSection>
              <ClearFiltersButton onClick={clearCertificateFilters}>
                <FiX />
                Clear Filters
              </ClearFiltersButton>
            </FilterSection>
          )}
          
          {hasCertificateFilters && (
            <ActiveFilters>
              <ActiveFiltersLabel>Active Filters:</ActiveFiltersLabel>
              {certificateStartDate && (
                <FilterTag>
                  <FiCalendar />
                  From: {formatKuwaitDate(certificateStartDate)}
                  <RemoveFilterButton onClick={() => setCertificateStartDate('')}>
                    <FiX size={12} />
                  </RemoveFilterButton>
                </FilterTag>
              )}
              {certificateEndDate && (
                <FilterTag>
                  <FiCalendar />
                  Until: {formatKuwaitDate(certificateEndDate)}
                  <RemoveFilterButton onClick={() => setCertificateEndDate('')}>
                    <FiX size={12} />
                  </RemoveFilterButton>
                </FilterTag>
              )}
            </ActiveFilters>
          )}
        </FilterControls>
        
        {loadingCertificates ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
            <Spinner />
            <LoadingText style={{ marginLeft: '12px' }}>Loading certificates...</LoadingText>
          </div>
        ) : pdfDownloads.length === 0 ? (
          <EmptyState>
            <EmptyStateTitle>No certificate downloads yet</EmptyStateTitle>
            <EmptyStateDescription>
              {hasCertificateFilters 
                ? 'No certificates found for the selected date range. Try adjusting your filters.'
                : 'Certificate PDF download records will appear here once users download their certificates.'}
            </EmptyStateDescription>
          </EmptyState>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell style={{ width: '50px' }}>
                  <input
                    type="checkbox"
                    data-select-all
                    checked={allSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                </TableHeaderCell>
                <TableHeaderCell>Candidate Name</TableHeaderCell>
                <TableHeaderCell>Course Title</TableHeaderCell>
                <TableHeaderCell>Score</TableHeaderCell>
                <TableHeaderCell>Downloaded At</TableHeaderCell>
                <TableHeaderCell>IP Address</TableHeaderCell>
              </TableRow>
            </TableHead>
            <tbody>
              {pdfDownloads.map((download) => (
              <TableRow key={download.id}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={selectedCertificates.has(download.certificate_id)}
                    onChange={(e) => handleSelectCertificate(download.certificate_id, e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                </TableCell>
                <TableCell>
                  <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '13px' }}>
                    {download.candidate_name}
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ color: '#374151', fontSize: '13px' }}>
                    {download.course_title || 'N/A'}
                  </div>
                </TableCell>
                <TableCell>
                  {download.percentage !== null && download.percentage !== undefined ? (
                    <ScoreBadge percentage={download.percentage}>
                      <FiAward />
                      {download.percentage?.toFixed(1)}%
                      {download.grade && (
                        <span style={{ opacity: 0.9, fontSize: '11px', marginLeft: '4px' }}>
                          ({download.grade})
                        </span>
                      )}
                    </ScoreBadge>
                  ) : (
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>N/A</span>
                  )}
                </TableCell>
                <TableCell>
                  <DateCell>
                    {formatKuwaitDate(download.downloaded_at)}
                    <TimeCell>
                      {formatKuwaitTime(download.downloaded_at)}
                    </TimeCell>
                  </DateCell>
                </TableCell>
                <TableCell>
                  <div style={{ 
                    fontFamily: 'monospace', 
                    fontSize: '12px', 
                    color: '#6b7280',
                    background: 'rgba(0, 0, 0, 0.05)',
                    padding: '4px 8px',
                    borderRadius: '4px'
                  }}>
                    {download.ip_address || 'N/A'}
                  </div>
                </TableCell>
              </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    );
  };

  const renderSettingsTab = () => {
    const expiryStatus = getExpiryStatus();
    const isExpired = expiryStatus.status === 'expired';
    
    return (
      <SettingsForm>
        <SettingsSection>
          <SettingsTitle>
            <FiSettings />
            Link Expiry Settings
          </SettingsTitle>
          
          {/* Current Status */}
          {form?.expiry_date && (
            <ExpiryStatus status={expiryStatus.status}>
              {expiryStatus.message}
            </ExpiryStatus>
          )}
          
          {/* Expired Form Actions */}
          {isExpired && (
            <div style={{ 
              background: '#fef2f2', 
              border: '1px solid #fecaca', 
              borderRadius: '8px', 
              padding: '16px', 
              marginBottom: '20px' 
            }}>
              <h4 style={{ color: '#dc2626', margin: '0 0 12px 0', fontSize: '12px' }}>
                Form Expired
              </h4>
              <p style={{ color: '#991b1b', margin: '0 0 16px 0', fontSize: '12px' }}>
                This form is no longer accepting responses. You can reactivate it by:
              </p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleReactivateForm()}
                  style={{
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#b91c1c'}
                  onMouseOut={(e) => e.target.style.background = '#dc2626'}
                >
                  Reactivate Form
                </button>
                <button
                  onClick={() => handleExtendExpiry()}
                  style={{
                    background: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#047857'}
                  onMouseOut={(e) => e.target.style.background = '#059669'}
                >
                  Extend Expiry
                </button>
              </div>
            </div>
          )}
          
          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={form?.is_active}
                onChange={(e) => handleToggleActive(e.target.checked)}
              />
              Form is active (accepting responses)
            </Label>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
              Uncheck to temporarily disable this form without deleting it
            </p>
          </FormGroup>

          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={settings.expiryEnabled}
                onChange={(e) => handleSettingsChange('expiryEnabled', e.target.checked)}
              />
              Enable link expiry
            </Label>
          </FormGroup>

          {settings.expiryEnabled && (
            <>
              <FormGroup>
                <Label htmlFor="expiryDate">Expiry Date</Label>
                <Input
                  type="date"
                  id="expiryDate"
                  value={settings.expiryDate}
                  onChange={(e) => handleSettingsChange('expiryDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="expiryTime">Expiry Time</Label>
                <Input
                  type="time"
                  id="expiryTime"
                  value={settings.expiryTime}
                  onChange={(e) => handleSettingsChange('expiryTime', e.target.value)}
                />
              </FormGroup>
            </>
          )}

          <SaveButton 
            onClick={handleSaveSettings} 
            disabled={savingSettings}
          >
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </SaveButton>
        </SettingsSection>
      </SettingsForm>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'responses':
        return renderResponsesTab();
      case 'analytics':
        return renderAnalyticsTab();
      case 'form-info':
        return renderFormInfoTab();
      case 'export':
        return renderExportTab();
      case 'certificates':
        return renderCertificatesTab();
      case 'settings':
        return renderSettingsTab();
      default:
        return renderResponsesTab();
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isExportDropdownOpen && !event.target.closest('.export-dropdown')) {
        setIsExportDropdownOpen(false);
      }
      if (isSortMenuOpen && !event.target.closest('.sort-dropdown')) {
        setIsSortMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExportDropdownOpen, isSortMenuOpen]);

  // Note: Sorting and filtering should be done server-side for proper pagination
  // Client-side filtering is disabled to ensure proper pagination works
  // The server already handles sorting (ORDER BY submitted_at DESC)

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    setIsSortMenuOpen(false);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSortBy('date');
    setSortOrder('desc');
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const hasActiveFilters = startDate || endDate;
  
  // When filters change, reset to page 1 and reload
  useEffect(() => {
    if (startDate || endDate) {
      setCurrentPage(1);
    }
  }, [startDate, endDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading data for formId:', formId);
      
      // Load form details
      console.log('📋 Loading form details...');
      const formResponse = await formsAPI.getById(formId);
      console.log('📋 Form API response:', formResponse);
      const formData = formResponse.data.data;
      
      // Parse form_structure if it's a JSON string
      if (formData.form_structure && typeof formData.form_structure === 'string') {
        try {
          formData.form_structure = JSON.parse(formData.form_structure);
          console.log('📋 Parsed form_structure:', formData.form_structure);
        } catch (e) {
          console.error('📋 Failed to parse form_structure:', e);
        }
      }
      
      // Don't convert to array - preserve object structure for questionnaires
      // Questionnaires have form_structure as an object with formFields and questions arrays
      // Regular forms have form_structure as an array of fields
      if (!formData.form_structure) {
        console.warn('📋 form_structure is missing, setting to empty array');
        formData.form_structure = [];
      } else if (typeof formData.form_structure !== 'object') {
        console.warn('📋 form_structure is not an object/array:', formData.form_structure);
        formData.form_structure = [];
      }
      
      // Log form structure (handle both array and object formats)
      if (Array.isArray(formData.form_structure)) {
        console.log('📋 Form structure fields (array):', formData.form_structure.map(f => ({
          id: f.id,
          name: f.name,
          label: f.label,
          type: f.type
        })));
      } else if (formData.form_structure && typeof formData.form_structure === 'object') {
        console.log('📋 Form structure (object):', {
          hasFormFields: !!formData.form_structure.formFields,
          formFieldsCount: formData.form_structure.formFields?.length || 0,
          hasQuestions: !!formData.form_structure.questions,
          questionsCount: formData.form_structure.questions?.length || 0,
          isQuestionnaire: formData.form_structure.isQuestionnaire
        });
        if (formData.form_structure.formFields) {
          console.log('📋 Form fields:', formData.form_structure.formFields.map(f => ({
            id: f.id,
            name: f.name,
            label: f.label,
            type: f.type
          })));
        }
        if (formData.form_structure.questions) {
          console.log('📋 Questions:', formData.form_structure.questions.map(q => ({
            id: q.id,
            question: q.question,
            type: q.type
          })));
        }
      }
      
      setForm(formData);

      // Load responses
      console.log('📊 Loading responses...');
      const responsesResponse = await responsesAPI.getByForm(formId, currentPage, pageSize);
      console.log('📋 Responses API response:', responsesResponse);
      console.log('📊 Responses data:', responsesResponse.data.data);
      console.log('📝 Individual responses:', responsesResponse.data.data.responses);
      
      setResponses(responsesResponse.data.data.responses);
      setTotalPages(responsesResponse.data.data.pagination.pages);
      setTotalResponses(responsesResponse.data.data.pagination.total);
    } catch (error) {
      console.error('❌ Load responses error:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      toast.error('Failed to load responses: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };


  const handleExport = async (format) => {
    try {
      setIsExportDropdownOpen(false);
      console.log(`Starting ${format} export for form ${formId}`);
      
      // Prepare date filters for export
      const filters = {};
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      
      let response;
      let filename;
      let mimeType;

      switch (format) {
        case 'csv':
          console.log('Exporting CSV...');
          response = await responsesAPI.exportCSV(formId, filters);
          filename = `${form.title}_responses.csv`;
          mimeType = 'text/csv';
          break;
        case 'json':
          console.log('Exporting JSON...');
          response = await responsesAPI.exportJSON(formId, filters);
          filename = `${form.title}_responses.json`;
          mimeType = 'application/json';
          break;
        case 'excel':
          console.log('Exporting Excel...');
          response = await responsesAPI.exportExcel(formId, filters);
          filename = `${form.title}_responses.xlsx`;
          mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'pdf':
          console.log('Exporting PDF...');
          response = await responsesAPI.exportPDF(formId, filters);
          filename = `${form.title}_responses.pdf`;
          mimeType = 'application/pdf';
          break;
        default:
          throw new Error('Unsupported export format');
      }

      console.log('Export response received:', response);
      console.log('Creating blob with MIME type:', mimeType);
      
      const blob = new Blob([response.data], { type: mimeType });
      downloadFile(blob, filename, mimeType);
      
      toast.success(`Responses exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      console.error('Export error details:', error);
      console.error('Error response:', error.response);
      toast.error(`Failed to export responses as ${format.toUpperCase()}: ${error.message}`);
    }
  };

  // Legacy function for backward compatibility
  const handleExportCSV = () => handleExport('csv');

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatResponseId = (id) => {
    return id.substring(0, 8) + '...';
  };

  const renderResponseValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return (
        <span style={{ 
          color: '#94a3b8', 
          fontStyle: 'italic',
          fontSize: '14px'
        }}>
          No response provided
        </span>
      );
    }
    
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return (
          <span style={{ 
            color: '#94a3b8', 
            fontStyle: 'italic',
            fontSize: '14px'
          }}>
            No selections made
          </span>
        );
      }
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {value.map((item, index) => (
            <span
              key={index}
              style={{
                background: '#e0f2fe',
                color: '#0369a1',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500',
                border: '1px solid #bae6fd'
              }}
            >
              {item}
            </span>
          ))}
        </div>
      );
    }
    
    if (typeof value === 'object') {
      return (
        <pre style={{
          background: '#f1f5f9',
          padding: '12px',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#475569',
          border: '1px solid #e2e8f0',
          overflow: 'auto',
          margin: 0
        }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    
    // Check if it's a URL
    if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) {
      return (
        <a 
          href={value} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{
            color: '#374151',
            textDecoration: 'none',
            fontWeight: '500'
          }}
        >
          {value}
        </a>
      );
    }
    
    // Check if it's an email
    if (typeof value === 'string' && value.includes('@') && value.includes('.')) {
      return (
        <a 
          href={`mailto:${value}`}
          style={{
            color: '#374151',
            textDecoration: 'none',
            fontWeight: '500'
          }}
        >
          {value}
        </a>
      );
    }
    
    return value;
  };

  const renderFileField = (value) => {
    const fileNames = parseFileNames(value);
    if (!fileNames || fileNames.length === 0) {
      return (
        <div style={{ color: '#6b7280', fontStyle: 'italic' }}>
          No files uploaded
        </div>
      );
    }

    return (
      <FileViewer>
        {fileNames.map((fileName, index) => (
          <FileItem key={index}>
            <FileInfo>
              <FiFile />
              <div>
                <FileName>{fileName}</FileName>
                <FileSize>{formatFileSize(null)}</FileSize>
              </div>
            </FileInfo>
            <FileActions>
              <OpenViewFileButton
                type="button"
                onClick={() => window.open(getFileViewURL(fileName), '_blank', 'noopener,noreferrer')}
                title="Open preview in a new tab"
              >
                <FiEye />
                View
              </OpenViewFileButton>
              <ViewFileButton type="button" onClick={() => downloadFile(fileName)}>
                <FiDownload />
                Download
              </ViewFileButton>
            </FileActions>
          </FileItem>
        ))}
      </FileViewer>
    );
  };

  const renderFieldItem = (item) => {
    if (!item) return null;

    const isEmptyDisplayValue = (value) => {
      if (value === null || value === undefined) return true;
      if (typeof value === 'string') return value.trim() === '';
      if (Array.isArray(value)) return value.length === 0;
      return false;
    };

    if (item.type === 'repeatable-field' && isEmptyDisplayValue(item.displayValue)) {
      return null;
    }

    if (item.type === 'matrix' && item.matrix) {
      return (
        <FieldRow key={item.key}>
          <FieldLabel>{item.label}</FieldLabel>
          <FieldValue>
            <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '8px' }}>
                <thead>
                  <tr>
                    {!item.matrix.field?.hideRowLabels && (
                      <th style={{ textAlign: 'left', fontSize: '12px', color: '#374151' }}></th>
                    )}
                    {(item.matrix.rows?.[0]?.cells || []).map((cell, index) => (
                      <th key={index} style={{ textAlign: 'left', fontSize: '12px', color: '#374151' }}>
                        {cell.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(item.matrix.rows || []).map((row) => (
                    <tr key={row.key}>
                      {!item.matrix.field?.hideRowLabels && (
                        <td style={{ padding: '6px 8px', color: '#374151', fontWeight: 500 }}>{row.label}</td>
                      )}
                      {row.cells.map((cell, index) => {
                        const cellValue = Array.isArray(cell.displayValue)
                          ? cell.displayValue.join(', ')
                          : (cell.displayValue && typeof cell.displayValue === 'object')
                            ? JSON.stringify(cell.displayValue)
                            : (cell.displayValue || '');
                        return (
                          <td key={index} style={{ padding: 0 }}>
                            <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '6px', padding: '6px', minHeight: '28px' }}>
                              <span style={{ color: '#374151' }}>{cellValue}</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FieldValue>
        </FieldRow>
      );
    }

    const isFile = item.field?.type === 'file';
    const isComment = item.type === 'comment';
    const fieldValueNode = isFile ? renderFileField(item.displayValue) : renderResponseValue(item.displayValue);

    return (
      <FieldRow key={item.key}>
        <FieldLabel>{item.label}</FieldLabel>
        <FieldValue style={isComment ? { fontStyle: 'italic', color: '#4b5563' } : undefined}>
          {fieldValueNode}
        </FieldValue>
      </FieldRow>
    );
  };


  if (loading) {
    return (
      <ResponsesContainer>
        <LoadingSpinner>
          <Spinner />
          <LoadingText>Loading responses...</LoadingText>
        </LoadingSpinner>
      </ResponsesContainer>
    );
  }

  if (!form) {
    return (
      <ResponsesContainer>
        <div>Form not found</div>
      </ResponsesContainer>
    );
  }

  return (
    <ResponsesContainer>
      <Header>
        <BackButton to="/">
          <FiArrowLeft />
          Back to Dashboard
        </BackButton>
        
        <HeaderInfo>
          <FormTitle>{form.title}</FormTitle>
          <FormDescription>
            {form.description || 'View and manage form responses'}
          </FormDescription>
        </HeaderInfo>

        <HeaderActions>
          <ActionButton onClick={loadData}>
            <FiRefreshCw />
            Refresh
          </ActionButton>
          
          <ExportDropdown className="export-dropdown">
            <ExportButton onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}>
              <FiDownload />
              Export
              <FiChevronDown />
            </ExportButton>
            
            <DropdownMenu isOpen={isExportDropdownOpen}>
              <DropdownItem onClick={() => handleExport('csv')}>
                <DropdownIcon color="#10b981">
                  <FiFileText />
                </DropdownIcon>
                <div>
                  <div style={{ fontWeight: '500' }}>CSV</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Spreadsheet format</div>
                </div>
              </DropdownItem>
              
              <DropdownItem onClick={() => handleExport('excel')}>
                <DropdownIcon color="#10b981">
                  <FiTable />
                </DropdownIcon>
                <div>
                  <div style={{ fontWeight: '500' }}>Excel</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Microsoft Excel (.xlsx)</div>
                </div>
              </DropdownItem>
              
              {/* JSON Export temporarily disabled */}
              {/* <DropdownItem onClick={() => handleExport('json')}>
                <DropdownIcon color="black">
                  <FiFile />
                </DropdownIcon>
                <div>
                  <div style={{ fontWeight: '500' }}>JSON</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Structured data format</div>
                </div>
              </DropdownItem> */}
              
              {/* PDF Export temporarily disabled */}
              {/* <DropdownItem onClick={() => handleExport('pdf')}>
                <DropdownIcon color="#ef4444">
                  <FiImage />
                </DropdownIcon>
                <div>
                  <div style={{ fontWeight: '500' }}>PDF</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Printable report</div>
                </div>
              </DropdownItem> */}
            </DropdownMenu>
          </ExportDropdown>
          
          <ActionButton onClick={() => window.open(`/form/${formId}`, '_blank')}>
            <FiEye />
            View Form
          </ActionButton>
        </HeaderActions>
      </Header>

      <TabContainer>
        <TabHeader>
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <TabIcon active={activeTab === tab.id}>{tab.icon}</TabIcon>
              {tab.label}
              {tab.count > 0 && (
                <TabBadge active={activeTab === tab.id}>
                  {tab.count}
                </TabBadge>
              )}
            </TabButton>
          ))}
        </TabHeader>
        
        <TabContent>
          {renderTabContent()}
        </TabContent>
      </TabContainer>

      {selectedResponse && (
        <ResponseModal onClick={() => setSelectedResponse(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <FiEye />
                Response Details
              </ModalTitle>
              <CloseButton onClick={() => setSelectedResponse(null)}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              {console.log('🔍 Rendering modal with selectedResponse:', selectedResponse)}
              {console.log('📋 selectedResponse.response_data:', selectedResponse.response_data)}
              {console.log('📋 response_data type:', typeof selectedResponse.response_data)}
              
              {/* Response metadata */}
              <div style={{ 
                padding: '16px', 
                background: 'rgba(0, 0, 0, 0.05)', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: '1px solid rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'black', marginBottom: '4px' }}>
                      📅 Response Date & Time
                    </div>
                  <div style={{ fontSize: '16px', color: '#374151' }}>
                    {formatKuwaitDateTime(selectedResponse.submitted_at).full}
                  </div>

                  </div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'black', marginBottom: '4px' }}>
                      🌐 IP Address
                    </div>
                    <div style={{ fontSize: '16px', color: '#374151' }}>
                      {selectedResponse.ip_address || 'Not available'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Form responses */}
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                {isQuestionnaire(form) ? '🎯 Questionnaire Response' : '📝 Form Responses'}
              </div>
              
              {/* Show questionnaire score if available */}
              {isQuestionnaire(form) && selectedResponse.response_data?._questionnaire_percentage !== undefined && (
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '14px', marginBottom: '8px', opacity: 0.9 }}>Assessment Result</div>
                  <div style={{ fontSize: '32px', fontWeight: '800' }}>
                    {selectedResponse.response_data._questionnaire_percentage?.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: '20px', marginTop: '8px', fontWeight: '600' }}>
                    {getRemark(selectedResponse.response_data._questionnaire_percentage)}
                  </div>
                  <div style={{ fontSize: '14px', marginTop: '8px', opacity: 0.9 }}>
                    {selectedResponse.response_data._questionnaire_score} / {selectedResponse.response_data._questionnaire_total} points
                  </div>
                  <div style={{ 
                    display: 'inline-block',
                    padding: '6px 16px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '20px',
                    marginTop: '12px',
                    fontSize: '16px',
                    fontWeight: '700'
                  }}>
                    Grade: {selectedResponse.response_data._questionnaire_grade}
                  </div>
                </div>
              )}
              
              {isQuestionnaire(form) ? (
                // Render questionnaire responses
                <div>
                  {/* Form Fields Responses */}
                  {form.form_structure?.formFields && form.form_structure.formFields.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                        User Information
                      </div>
                      {form.form_structure.formFields.map((field) => {
                        const fieldKey = field.name || field.id;
                        const value = selectedResponse.response_data[fieldKey];
                        
                        return (
                          <FieldRow key={fieldKey}>
                            <FieldLabel>{field.label}</FieldLabel>
                            <FieldValue>{value || '(No answer)'}</FieldValue>
                          </FieldRow>
                        );
                      })}
                    </div>
                  )}

                  {/* Question Responses */}
                  {form.form_structure?.questions && form.form_structure.questions.length > 0 && (
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                        Question Responses
                      </div>
                      {form.form_structure.questions.map((question, index) => {
                        const questionKey = question.id;
                        const userAnswer = selectedResponse.response_data[questionKey];
                        const questionResult = selectedResponse.response_data._questionnaire_results?.find(r => r.questionNumber === index + 1);
                        
                        return (
                          <div key={questionKey} style={{
                            padding: '16px',
                            marginBottom: '12px',
                            borderRadius: '8px',
                            borderLeft: `4px solid ${questionResult?.isCorrect ? '#10b981' : questionResult?.requiresManualGrading ? '#f59e0b' : '#ef4444'}`,
                            background: questionResult?.isCorrect ? '#f0fdf4' : questionResult?.requiresManualGrading ? '#fffbeb' : '#fef2f2'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                              <div style={{ fontWeight: '600', color: '#1f2937' }}>
                                {index + 1}. {question.question}
                              </div>
                              <div style={{ 
                                fontWeight: '700',
                                color: questionResult?.isCorrect ? '#059669' : questionResult?.requiresManualGrading ? '#d97706' : '#dc2626'
                              }}>
                                {questionResult?.requiresManualGrading ? 'Manual Review' : `${questionResult?.earnedPoints || 0}/${question.points}`}
                              </div>
                            </div>
                            <div style={{ fontSize: '14px', color: '#6b7280' }}>
                              <div><strong>User Answer:</strong> {Array.isArray(userAnswer) ? userAnswer.join(', ') : userAnswer || '(No answer)'}</div>
                              {!questionResult?.isCorrect && !questionResult?.requiresManualGrading && questionResult?.correctAnswer && (
                                <div style={{ color: '#059669', marginTop: '4px' }}>
                                  <strong>Correct Answer:</strong> {questionResult.correctAnswer}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                displayFields.length > 0 ? (
                  displayFields.map(renderFieldItem)
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  <p>No response data available for this response.</p>
                    <p>Response object keys: {Object.keys(selectedResponse.response_data || {}).join(', ')}</p>
                </div>
                )
              )}
            </ModalBody>
            <ModalFooter>
              <ModalButton onClick={() => setSelectedResponse(null)}>
                Close
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ResponseModal>
      )}

    </ResponsesContainer>
  );
}

export default Responses;
